/**
 * PRE-SCREENING SCORE SIMULATOR
 * 
 * Comprehensive credit score projection tool that helps clients understand
 * what their score could be with various improvements.
 * 
 * Features:
 * - Per-Bureau simulation (Equifax, Experian, TransUnion)
 * - Multiple AU tradelines with impact breakdown
 * - Credit card paydown simulator
 * - Derogatory item removal impact
 * - Email results to client via Gmail
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Calculator,
  TrendingUp,
  Mail,
  Download,
  Plus,
  Minus,
  X,
  ChevronDown,
  ChevronUp,
  Users,
  CreditCard,
  Clock,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Sparkles,
  Target,
  Info,
  RefreshCw,
  Home,
  Scale,
  DollarSign,
  Building,
  Percent,
  CheckCircle2,
  CheckCircle,
  ArrowRight,
  Wallet,
  Car,
  GraduationCap,
  MessageSquare,
  Bot,
  Wand2,
  Send,
} from "lucide-react";
import CreditOptimizationAdvisor from "../components/CreditOptimizationAdvisor";
import MobileNav from "../components/MobileNav";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Score color helpers
const getScoreColor = (score) => {
  if (score >= 800) return "text-green-400";
  if (score >= 740) return "text-emerald-400";
  if (score >= 670) return "text-blue-400";
  if (score >= 580) return "text-amber-400";
  return "text-red-400";
};

const getScoreBgColor = (score) => {
  if (score >= 800) return "bg-green-500/20 border-green-500/30";
  if (score >= 740) return "bg-emerald-500/20 border-emerald-500/30";
  if (score >= 670) return "bg-blue-500/20 border-blue-500/30";
  if (score >= 580) return "bg-amber-500/20 border-amber-500/30";
  return "bg-red-500/20 border-red-500/30";
};

const getTierName = (score) => {
  if (score >= 800) return "Exceptional";
  if (score >= 740) return "Very Good";
  if (score >= 670) return "Good";
  if (score >= 580) return "Fair";
  return "Poor";
};

const BUREAUS = ["EQUIFAX", "EXPERIAN", "TRANSUNION"];

export default function PreScreening() {
  const navigate = useNavigate();
  
  // ═══════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════
  
  // Client info for email
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  
  // NEW: Data Confidence Level (affects projected ranges)
  // Default to HIGH since users typically have actual credit report
  const [confidenceLevel, setConfidenceLevel] = useState("high");
  
  // NEW: Goal Mode Presets
  const [goalMode, setGoalMode] = useState(null);
  
  // NEW: Reporting Timing Toggle
  const [useStatementBalances, setUseStatementBalances] = useState(true);
  
  // NEW: Fastest Win Toggle
  const [prioritizeFastestWins, setPrioritizeFastestWins] = useState(false);
  
  // Which bureaus are enabled for simulation
  const [enabledBureaus, setEnabledBureaus] = useState({
    EQUIFAX: true,
    EXPERIAN: true,
    TRANSUNION: true,
  });
  
  // Current scores per bureau
  const [currentScores, setCurrentScores] = useState({
    EQUIFAX: 580,
    EXPERIAN: 580,
    TRANSUNION: 580,
  });
  
  // Derogatory counts per bureau - ENHANCED with age buckets and balance tracking
  // "total" = how many the client currently has
  // "to_remove" = how many we're disputing/removing
  // "age_bucket" = most recent age (0-6, 7-12, 13-24, 25-48, 49+)
  // "total_balance" = total balance on these items
  // "has_balance" = for charge-offs, whether any are still reporting a balance
  const [derogs, setDerogs] = useState({
    EQUIFAX: {
      collections: { total: 0, to_remove: 0, age_bucket: "13-24", total_balance: 0 },
      chargeoffs: { total: 0, to_remove: 0, age_bucket: "13-24", total_balance: 0, has_balance: false },
      late30: { total: 0, to_remove: 0, age_bucket: "13-24" },
      late60: { total: 0, to_remove: 0, age_bucket: "13-24" },
      late90plus: { total: 0, to_remove: 0, age_bucket: "13-24" },
      student_loans: { total: 0, to_remove: 0, delinquent: false },
      auto_repo: { total: 0, to_remove: 0, age_bucket: "13-24", has_deficiency: false },
      auto_closure_balance: { total: 0, to_remove: 0 },
      mortgage_foreclosure: { total: 0, to_remove: 0, age_bucket: "13-24" },
    },
    EXPERIAN: {
      collections: { total: 0, to_remove: 0, age_bucket: "13-24", total_balance: 0 },
      chargeoffs: { total: 0, to_remove: 0, age_bucket: "13-24", total_balance: 0, has_balance: false },
      late30: { total: 0, to_remove: 0, age_bucket: "13-24" },
      late60: { total: 0, to_remove: 0, age_bucket: "13-24" },
      late90plus: { total: 0, to_remove: 0, age_bucket: "13-24" },
      student_loans: { total: 0, to_remove: 0, delinquent: false },
      auto_repo: { total: 0, to_remove: 0, age_bucket: "13-24", has_deficiency: false },
      auto_closure_balance: { total: 0, to_remove: 0 },
      mortgage_foreclosure: { total: 0, to_remove: 0, age_bucket: "13-24" },
    },
    TRANSUNION: {
      collections: { total: 0, to_remove: 0, age_bucket: "13-24", total_balance: 0 },
      chargeoffs: { total: 0, to_remove: 0, age_bucket: "13-24", total_balance: 0, has_balance: false },
      late30: { total: 0, to_remove: 0, age_bucket: "13-24" },
      late60: { total: 0, to_remove: 0, age_bucket: "13-24" },
      late90plus: { total: 0, to_remove: 0, age_bucket: "13-24" },
      student_loans: { total: 0, to_remove: 0, delinquent: false },
      auto_repo: { total: 0, to_remove: 0, age_bucket: "13-24", has_deficiency: false },
      auto_closure_balance: { total: 0, to_remove: 0 },
      mortgage_foreclosure: { total: 0, to_remove: 0, age_bucket: "13-24" },
    },
  });
  
  // AI Agent mode state
  const [agentMode, setAgentMode] = useState(false);
  const [agentInput, setAgentInput] = useState('');
  const [agentAnalysis, setAgentAnalysis] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  
  // NEW: Two-step formatting flow
  const [formattedData, setFormattedData] = useState(null); // Parsed & formatted breakdown
  const [isFormatting, setIsFormatting] = useState(false);
  const [showFormatted, setShowFormatted] = useState(false); // Show formatted view vs raw input
  
  // Credit Cards for paydown simulation
  const [creditCards, setCreditCards] = useState([]);
  
  // NEW: Positive Tradelines - Auto Loans, Personal Loans, Zero Balance Cards
  const [autoLoans, setAutoLoans] = useState([]);
  const [personalLoans, setPersonalLoans] = useState([]);
  const [zeroBalanceCards, setZeroBalanceCards] = useState([]);
  
  // Authorized User Tradelines - ENHANCED with quality inputs
  // AU age bucket: <2, 2-5, 5-10, 10+
  // AU utilization bucket: <10, 10-30, 30-70, 70+
  // AU has lates: yes/no
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  
  // File data (inquiries, age)
  const [fileData, setFileData] = useState({
    inquiries_0_12mo: 0,
    avg_age_years: null,
    oldest_age_years: null,
    revolving_accounts_count: 5,
  });
  
  // Active bureau tab (for derog entry)
  const [activeBureau, setActiveBureau] = useState("EQUIFAX");
  
  // Scoring model
  const [model, setModel] = useState("FICO8");
  
  // Show 10% utilization projection comparison
  const [show10PercentProjection, setShow10PercentProjection] = useState(false);
  
  // UI State
  const [simulating, setSimulating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    scores: true,
    derogs: true,
    cards: true,
    autoLoans: false,
    personalLoans: false,
    zeroBalanceCards: false,
    au: true,
  });
  
  // Get list of enabled bureaus
  const getEnabledBureaus = () => BUREAUS.filter(b => enabledBureaus[b]);
  
  // Toggle bureau enabled state
  const toggleBureau = (bureau) => {
    setEnabledBureaus(prev => ({
      ...prev,
      [bureau]: !prev[bureau]
    }));
    // If disabling active bureau, switch to first enabled one
    if (enabledBureaus[bureau] && activeBureau === bureau) {
      const remaining = BUREAUS.filter(b => b !== bureau && enabledBureaus[b]);
      if (remaining.length > 0) {
        setActiveBureau(remaining[0]);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // HELPER COMPONENTS
  // ═══════════════════════════════════════════════════════════════════
  
  const CounterInput = ({ value, onChange, label, min = 0, max = 99, small = false }) => (
    <div className={`flex items-center ${small ? 'gap-1' : 'gap-2'}`}>
      <Button
        type="button"
        size="icon"
        variant="outline"
        className={`${small ? 'h-6 w-6' : 'h-7 w-7'} border-zinc-600`}
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
      >
        <Minus className={`${small ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
      </Button>
      <Input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const newVal = parseInt(e.target.value) || 0;
          onChange(Math.max(min, Math.min(max, newVal)));
        }}
        className={`${small ? 'w-10 h-6 text-sm' : 'w-14 h-8'} text-center font-mono font-bold bg-zinc-800 border-zinc-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
      />
      <Button
        type="button"
        size="icon"
        variant="outline"
        className={`${small ? 'h-6 w-6' : 'h-7 w-7'} border-zinc-600`}
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
      >
        <Plus className={`${small ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
      </Button>
      <span className={`${small ? 'text-[10px]' : 'text-xs'} text-zinc-400 ml-1`}>{label}</span>
    </div>
  );
  
  // New: Derog Input with Total and To Remove
  const DerogInputPair = ({ bureau, field, label, color = 'red', showAge = false, showBalance = false, showHasBalance = false, showDeficiency = false, showDelinquent = false }) => {
    const item = derogs[bureau][field];
    const remaining = (item.total || 0) - (item.to_remove || 0);
    const colorClasses = {
      red: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
      orange: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
      yellow: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
      blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
    };
    const colors = colorClasses[color] || colorClasses.red;
    
    return (
      <div className={`p-2 rounded-lg ${colors.bg} border ${colors.border}`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold ${colors.text}`}>{label}</span>
          {item.total > 0 && item.to_remove < item.total && (
            <button
              onClick={() => setRemoveAll(bureau, field)}
              className="text-[10px] text-green-400 hover:text-green-300 underline"
            >
              Remove All
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {/* Total (How many they have) */}
          <div>
            <div className="text-[10px] text-zinc-500 mb-1">Have</div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-7 w-7 border-zinc-600 flex-shrink-0"
                onClick={() => updateDerog(bureau, field, 'total', (item.total || 0) - 1)}
                disabled={(item.total || 0) <= 0}
              >
                <Minus className="w-3 h-3" />
              </Button>
              <Input
                type="number"
                min={0}
                max={99}
                value={item.total || 0}
                onChange={(e) => updateDerog(bureau, field, 'total', parseInt(e.target.value) || 0)}
                className="w-12 h-7 text-sm text-center font-mono font-bold bg-zinc-800 border-zinc-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-7 w-7 border-zinc-600 flex-shrink-0"
                onClick={() => updateDerog(bureau, field, 'total', (item.total || 0) + 1)}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
          
          {/* To Remove (How many to dispute) */}
          <div>
            <div className="text-[10px] text-green-400 mb-1">Remove</div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-7 w-7 border-green-600 flex-shrink-0"
                onClick={() => updateDerog(bureau, field, 'to_remove', (item.to_remove || 0) - 1)}
                disabled={(item.to_remove || 0) <= 0}
              >
                <Minus className="w-3 h-3" />
              </Button>
              <Input
                type="number"
                min={0}
                max={item.total || 0}
                value={item.to_remove || 0}
                onChange={(e) => updateDerog(bureau, field, 'to_remove', parseInt(e.target.value) || 0)}
                className="w-12 h-7 text-sm text-center font-mono font-bold bg-green-900/30 border-green-600 text-green-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-7 w-7 border-green-600 flex-shrink-0"
                onClick={() => updateDerog(bureau, field, 'to_remove', (item.to_remove || 0) + 1)}
                disabled={(item.to_remove || 0) >= (item.total || 0)}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Age Bucket Selector - NEW */}
        {showAge && item.total > 0 && (
          <div className="mt-2">
            <div className="text-[10px] text-zinc-500 mb-1">Most Recent Age</div>
            <Select
              value={item.age_bucket || "13-24"}
              onValueChange={(val) => updateDerog(bureau, field, 'age_bucket', val)}
            >
              <SelectTrigger className="h-6 text-xs bg-zinc-800 border-zinc-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0-6">0-6 months (Recent)</SelectItem>
                <SelectItem value="7-12">7-12 months</SelectItem>
                <SelectItem value="13-24">13-24 months</SelectItem>
                <SelectItem value="25-48">25-48 months</SelectItem>
                <SelectItem value="49+">49+ months (Old)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Total Balance - For collections/charge-offs */}
        {showBalance && item.total > 0 && (
          <div className="mt-2">
            <div className="text-[10px] text-zinc-500 mb-1">Total Balance</div>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-500">$</span>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                defaultValue={item.total_balance || ''}
                placeholder="0"
                onBlur={(e) => {
                  const val = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                  updateDerog(bureau, field, 'total_balance', val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
                className="h-6 text-xs pl-5 bg-zinc-800 border-zinc-600"
              />
            </div>
          </div>
        )}
        
        {/* Has Balance Toggle - For charge-offs */}
        {showHasBalance && item.total > 0 && (
          <label className="mt-2 flex items-center gap-2 cursor-pointer">
            <Switch
              checked={item.has_balance || false}
              onCheckedChange={(checked) => updateDerog(bureau, field, 'has_balance', checked)}
              className="h-4 w-7"
            />
            <span className="text-[10px] text-zinc-400">Reporting balance?</span>
          </label>
        )}
        
        {/* Has Deficiency - For repos */}
        {showDeficiency && item.total > 0 && (
          <label className="mt-2 flex items-center gap-2 cursor-pointer">
            <Switch
              checked={item.has_deficiency || false}
              onCheckedChange={(checked) => updateDerog(bureau, field, 'has_deficiency', checked)}
              className="h-4 w-7"
            />
            <span className="text-[10px] text-zinc-400">Has deficiency balance?</span>
          </label>
        )}
        
        {/* Is Delinquent - For student loans */}
        {showDelinquent && item.total > 0 && (
          <label className="mt-2 flex items-center gap-2 cursor-pointer">
            <Switch
              checked={item.delinquent || false}
              onCheckedChange={(checked) => updateDerog(bureau, field, 'delinquent', checked)}
              className="h-4 w-7"
            />
            <span className="text-[10px] text-zinc-400">Currently delinquent?</span>
          </label>
        )}
        
        {/* Remaining indicator */}
        {remaining > 0 && (
          <div className="mt-2 text-[10px] text-amber-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {remaining} remaining (still affects score)
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // DEROGATORY ITEM HANDLERS
  // ═══════════════════════════════════════════════════════════════════
  
  // Update derog with new structure (total, to_remove, age_bucket, etc.)
  const updateDerog = (bureau, field, subfield, value) => {
    setDerogs((prev) => {
      const currentItem = prev[bureau][field];
      
      // If updating total, ensure to_remove doesn't exceed it
      if (subfield === 'total') {
        const newValue = Math.max(0, Math.min(99, value));
        return {
          ...prev,
          [bureau]: {
            ...prev[bureau],
            [field]: {
              ...currentItem,
              total: newValue,
              to_remove: Math.min(currentItem.to_remove || 0, newValue),
            },
          },
        };
      }
      
      // If updating to_remove, ensure it doesn't exceed total
      if (subfield === 'to_remove') {
        const newValue = Math.max(0, Math.min(99, value));
        return {
          ...prev,
          [bureau]: {
            ...prev[bureau],
            [field]: {
              ...currentItem,
              to_remove: Math.min(newValue, currentItem.total || 0),
            },
          },
        };
      }
      
      // For all other fields (age_bucket, total_balance, has_balance, has_deficiency, delinquent)
      return {
        ...prev,
        [bureau]: {
          ...prev[bureau],
          [field]: {
            ...currentItem,
            [subfield]: value,
          },
        },
      };
    });
  };
  
  // Quick set: set to_remove equal to total (remove all)
  const setRemoveAll = (bureau, field) => {
    setDerogs((prev) => ({
      ...prev,
      [bureau]: {
        ...prev[bureau],
        [field]: {
          ...prev[bureau][field],
          to_remove: prev[bureau][field].total,
        },
      },
    }));
  };
  
  const copyDerogsToAllBureaus = () => {
    const sourceBureau = getEnabledBureaus()[0] || 'EQUIFAX';
    const sourceDerogs = derogs[sourceBureau];
    setDerogs({
      EQUIFAX: { ...sourceDerogs },
      EXPERIAN: { ...sourceDerogs },
      TRANSUNION: { ...sourceDerogs },
    });
    toast.success(`Copied derogatory items from ${sourceBureau} to all bureaus`);
  };

  // ═══════════════════════════════════════════════════════════════════
  // CREDIT CARD HANDLERS
  // ═══════════════════════════════════════════════════════════════════
  
  const addCreditCard = () => {
    setCreditCards([
      ...creditCards,
      {
        id: Date.now(),
        name: "",
        credit_limit: 5000,
        current_balance: 2500,
        target_balance: 500,
      },
    ]);
  };
  
  const updateCreditCard = (index, field, value) => {
    const updated = [...creditCards];
    updated[index][field] = value;
    setCreditCards(updated);
  };
  
  const removeCreditCard = (index) => {
    setCreditCards(creditCards.filter((_, i) => i !== index));
  };
  
  // Calculate utilization
  const calculateUtilization = (balance, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.round((balance / limit) * 100);
  };
  
  // Calculate total utilization across all cards
  const getTotalUtilization = (useTarget = false) => {
    if (creditCards.length === 0) return null;
    const totalLimit = creditCards.reduce((sum, c) => sum + (c.credit_limit || 0), 0);
    const totalBalance = creditCards.reduce(
      (sum, c) => sum + (useTarget ? (c.target_balance || 0) : (c.current_balance || 0)),
      0
    );
    if (totalLimit === 0) return 0;
    return Math.round((totalBalance / totalLimit) * 100);
  };

  // ═══════════════════════════════════════════════════════════════════
  // POSITIVE TRADELINES HANDLERS - Auto Loans, Personal Loans, Zero Balance Cards
  // ═══════════════════════════════════════════════════════════════════
  
  const addAutoLoan = () => {
    setAutoLoans([
      ...autoLoans,
      {
        id: Date.now(),
        lender_name: "",
        original_amount: 25000,
        current_balance: 15000,
        monthly_payment: 450,
        date_opened: "",
        on_time_payments: true,
        months_paid: 24,
      },
    ]);
  };
  
  const updateAutoLoan = (index, field, value) => {
    const updated = [...autoLoans];
    updated[index][field] = value;
    setAutoLoans(updated);
  };
  
  const removeAutoLoan = (index) => {
    setAutoLoans(autoLoans.filter((_, i) => i !== index));
  };
  
  const addPersonalLoan = () => {
    setPersonalLoans([
      ...personalLoans,
      {
        id: Date.now(),
        lender_name: "",
        original_amount: 10000,
        current_balance: 5000,
        monthly_payment: 300,
        date_opened: "",
        on_time_payments: true,
        months_paid: 12,
      },
    ]);
  };
  
  const updatePersonalLoan = (index, field, value) => {
    const updated = [...personalLoans];
    updated[index][field] = value;
    setPersonalLoans(updated);
  };
  
  const removePersonalLoan = (index) => {
    setPersonalLoans(personalLoans.filter((_, i) => i !== index));
  };
  
  const addZeroBalanceCard = () => {
    setZeroBalanceCards([
      ...zeroBalanceCards,
      {
        id: Date.now(),
        card_name: "",
        credit_limit: 5000,
        date_opened: "",
        age_years: 3,
        on_time_payments: true,
      },
    ]);
  };
  
  const updateZeroBalanceCard = (index, field, value) => {
    const updated = [...zeroBalanceCards];
    updated[index][field] = value;
    setZeroBalanceCards(updated);
  };
  
  const removeZeroBalanceCard = (index) => {
    setZeroBalanceCards(zeroBalanceCards.filter((_, i) => i !== index));
  };

  // ═══════════════════════════════════════════════════════════════════
  // AUTHORIZED USER HANDLERS - ENHANCED with quality inputs
  // ═══════════════════════════════════════════════════════════════════
  
  const addAU = () => {
    setAuthorizedUsers([
      ...authorizedUsers,
      {
        id: Date.now(),
        card_name: "",
        au_age_bucket: "5-10",  // <2, 2-5, 5-10, 10+
        au_utilization_bucket: "<10",  // <10, 10-30, 30-70, 70+
        au_has_lates: false,
        // Legacy fields for backward compatibility
        au_history_years: 7,
        au_credit_limit: 15000,
        au_utilization: 5,
        au_is_clean: true,
      },
    ]);
  };
  
  const updateAU = (index, field, value) => {
    const updated = [...authorizedUsers];
    updated[index][field] = value;
    setAuthorizedUsers(updated);
  };
  
  const removeAU = (index) => {
    setAuthorizedUsers(authorizedUsers.filter((_, i) => i !== index));
  };

  // ═══════════════════════════════════════════════════════════════════
  // AI CREDIT REPAIR AGENT - STEP 1: FORMAT INPUT
  // Takes raw text and organizes it into structured, editable sections
  // ═══════════════════════════════════════════════════════════════════
  
  const formatInputText = async () => {
    if (!agentInput.trim()) {
      toast.error("Please enter text to format");
      return;
    }
    
    setIsFormatting(true);
    
    try {
      // Call AI to parse and organize the input
      const response = await axios.post(`${API}/prescreening/ai-format`, {
        raw_text: agentInput,
      });
      
      const formatted = response.data;
      setFormattedData(formatted);
      setShowFormatted(true);
      toast.success("Text formatted! Review and edit if needed, then run simulation.");
      
    } catch (error) {
      console.error("Format error:", error);
      // Fallback: Parse locally
      const localFormatted = formatTextLocally(agentInput);
      setFormattedData(localFormatted);
      setShowFormatted(true);
      toast.success("Text formatted! Review and edit below.");
    } finally {
      setIsFormatting(false);
    }
  };
  
  // Local formatting fallback
  const formatTextLocally = (text) => {
    const lowText = text.toLowerCase();
    const formatted = {
      bureaus_mentioned: [],
      scores: {},
      charge_offs: { credit_cards: 0, loans: 0, other: 0, total: 0 },
      collections: { count: 0, total_balance: 0 },
      late_payments: { thirty_day: 0, sixty_day: 0, ninety_plus: 0 },
      repos: 0,
      foreclosures: 0,
      student_loans: { count: 0, delinquent: false },
      credit_cards: [],
      inquiries: 0,
      timeline: "72-120 days",
      raw_text: text,
    };
    
    // Detect bureaus mentioned
    if (lowText.includes('equifax') || lowText.includes(' eq ')) {
      formatted.bureaus_mentioned.push('EQUIFAX');
    }
    if (lowText.includes('experian') || lowText.includes(' ex ')) {
      formatted.bureaus_mentioned.push('EXPERIAN');
    }
    if (lowText.includes('transunion') || lowText.includes('trans union') || lowText.includes(' tu ')) {
      formatted.bureaus_mentioned.push('TRANSUNION');
    }
    
    // Parse scores
    const eqMatch = lowText.match(/equifax[:\s]*(\d{3})/);
    const exMatch = lowText.match(/experian[:\s]*(\d{3})/);
    const tuMatch = lowText.match(/transunion[:\s]*(\d{3})|trans\s*union[:\s]*(\d{3})/);
    
    if (eqMatch) formatted.scores.EQUIFAX = parseInt(eqMatch[1]);
    if (exMatch) formatted.scores.EXPERIAN = parseInt(exMatch[1]);
    if (tuMatch) formatted.scores.TRANSUNION = parseInt(tuMatch[1] || tuMatch[2]);
    
    // Parse charge-offs - differentiate between credit cards and loans
    // Look for patterns like "5 charged off credit cards" or "5 charge off loans"
    const coCardMatch = lowText.match(/(\d+)\s*charge[d\s-]*off[s]?\s*(credit\s*)?card/);
    const coLoanMatch = lowText.match(/(\d+)\s*charge[d\s-]*off[s]?\s*loan/);
    const coGenericMatch = lowText.match(/(\d+)\s*charge[\s-]*off/);
    
    if (coCardMatch) {
      formatted.charge_offs.credit_cards = parseInt(coCardMatch[1]);
    }
    if (coLoanMatch) {
      formatted.charge_offs.loans = parseInt(coLoanMatch[1]);
    }
    if (coGenericMatch && !coCardMatch && !coLoanMatch) {
      // Generic charge-off mention
      formatted.charge_offs.other = parseInt(coGenericMatch[1]);
    }
    formatted.charge_offs.total = formatted.charge_offs.credit_cards + formatted.charge_offs.loans + formatted.charge_offs.other;
    
    // Parse collections
    const collMatch = lowText.match(/(\d+)\s*collection/);
    if (collMatch) formatted.collections.count = parseInt(collMatch[1]);
    
    // Parse late payments
    const late30Match = lowText.match(/(\d+)\s*30[\s-]*day\s*late/);
    const late60Match = lowText.match(/(\d+)\s*60[\s-]*day\s*late/);
    const late90Match = lowText.match(/(\d+)\s*(90|90\+)[\s-]*day\s*late/);
    
    if (late30Match) formatted.late_payments.thirty_day = parseInt(late30Match[1]);
    if (late60Match) formatted.late_payments.sixty_day = parseInt(late60Match[1]);
    if (late90Match) formatted.late_payments.ninety_plus = parseInt(late90Match[1]);
    
    // Parse repos and foreclosures
    const repoMatch = lowText.match(/(\d+)\s*repo/);
    const fcMatch = lowText.match(/(\d+)\s*foreclosure/);
    if (repoMatch) formatted.repos = parseInt(repoMatch[1]);
    if (fcMatch) formatted.foreclosures = parseInt(fcMatch[1]);
    
    return formatted;
  };
  
  // Update formatted data field
  const updateFormattedField = (path, value) => {
    setFormattedData(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      
      // Recalculate totals if updating charge_offs
      if (path.startsWith('charge_offs.') && path !== 'charge_offs.total') {
        updated.charge_offs.total = 
          (updated.charge_offs.credit_cards || 0) + 
          (updated.charge_offs.loans || 0) + 
          (updated.charge_offs.other || 0);
      }
      
      return updated;
    });
  };
  
  // Clear formatted view and go back to input
  const clearFormatted = () => {
    setFormattedData(null);
    setShowFormatted(false);
  };
  
  // Run simulation from formatted data
  const runSimulationFromFormatted = async () => {
    if (!formattedData) return;
    
    // Convert formatted data to the format expected by parseAgentScenario
    const convertedInput = buildInputFromFormatted(formattedData);
    
    // Update enabled bureaus based on what was mentioned
    if (formattedData.bureaus_mentioned && formattedData.bureaus_mentioned.length > 0) {
      const newEnabled = { EQUIFAX: false, EXPERIAN: false, TRANSUNION: false };
      formattedData.bureaus_mentioned.forEach(b => {
        newEnabled[b] = true;
      });
      setEnabledBureaus(newEnabled);
    }
    
    // Run the simulation
    await parseAgentScenario(convertedInput);
  };
  
  // Build input text from formatted data (for the existing parser)
  const buildInputFromFormatted = (data) => {
    let parts = [];
    
    // Add bureau scores
    if (data.scores) {
      Object.entries(data.scores).forEach(([bureau, score]) => {
        if (score) parts.push(`${bureau}: ${score}`);
      });
    }
    
    // Add charge-offs with breakdown
    if (data.charge_offs.total > 0) {
      if (data.charge_offs.credit_cards > 0) {
        parts.push(`${data.charge_offs.credit_cards} charged off credit cards`);
      }
      if (data.charge_offs.loans > 0) {
        parts.push(`${data.charge_offs.loans} charged off loans`);
      }
      if (data.charge_offs.other > 0) {
        parts.push(`${data.charge_offs.other} charge-offs`);
      }
    }
    
    // Add collections
    if (data.collections.count > 0) {
      parts.push(`${data.collections.count} collections`);
    }
    
    // Add late payments
    if (data.late_payments.thirty_day > 0) {
      parts.push(`${data.late_payments.thirty_day} 30-day late payments`);
    }
    if (data.late_payments.sixty_day > 0) {
      parts.push(`${data.late_payments.sixty_day} 60-day late payments`);
    }
    if (data.late_payments.ninety_plus > 0) {
      parts.push(`${data.late_payments.ninety_plus} 90+ day late payments`);
    }
    
    // Add repos and foreclosures
    if (data.repos > 0) parts.push(`${data.repos} repossessions`);
    if (data.foreclosures > 0) parts.push(`${data.foreclosures} foreclosures`);
    
    return parts.join('. ');
  };

  // ═══════════════════════════════════════════════════════════════════
  // AI CREDIT REPAIR AGENT - STEP 2: Parse and run simulation
  // ENHANCED with confidence levels, goal modes, and action plans
  // ═══════════════════════════════════════════════════════════════════
  
  const parseAgentScenario = async (inputText) => {
    setAgentLoading(true);
    
    try {
      // Call backend AI endpoint to parse the scenario with enhanced params
      const response = await axios.post(`${API}/prescreening/ai-analyze`, {
        scenario_text: inputText,
        confidence_level: confidenceLevel,
        goal_mode: goalMode,
      });
      
      const parsed = response.data;
      
      // Apply parsed data to form
      if (parsed.scores) {
        const newScores = { ...currentScores };
        const newEnabled = { ...enabledBureaus };
        
        if (parsed.scores.equifax) {
          newScores.EQUIFAX = parsed.scores.equifax;
          newEnabled.EQUIFAX = true;
        }
        if (parsed.scores.experian) {
          newScores.EXPERIAN = parsed.scores.experian;
          newEnabled.EXPERIAN = true;
        }
        if (parsed.scores.transunion) {
          newScores.TRANSUNION = parsed.scores.transunion;
          newEnabled.TRANSUNION = true;
        }
        
        setCurrentScores(newScores);
        setEnabledBureaus(newEnabled);
      }
      
      // Apply derogatory items with age buckets
      if (parsed.derogs) {
        const newDerogs = { ...derogs };
        const activeBureaus = Object.keys(enabledBureaus).filter(b => enabledBureaus[b]);
        
        activeBureaus.forEach(bureau => {
          // Collections with age bucket
          if (parsed.derogs.collections) {
            const coll = typeof parsed.derogs.collections === 'object' ? parsed.derogs.collections : { count: parsed.derogs.collections };
            newDerogs[bureau].collections = { 
              total: coll.count || 0, 
              to_remove: 0,
              age_bucket: coll.age_bucket || "13-24",
              total_balance: coll.total_balance || 0,
            };
          }
          // Charge-offs with age bucket and balance tracking
          if (parsed.derogs.chargeoffs) {
            const co = typeof parsed.derogs.chargeoffs === 'object' ? parsed.derogs.chargeoffs : { count: parsed.derogs.chargeoffs };
            newDerogs[bureau].chargeoffs = { 
              total: co.count || 0, 
              to_remove: 0,
              age_bucket: co.age_bucket || "13-24",
              total_balance: co.total_balance || 0,
              has_balance: co.has_balance !== false,
            };
          }
          // Late payments with age buckets
          if (parsed.derogs.late30) {
            const late = typeof parsed.derogs.late30 === 'object' ? parsed.derogs.late30 : { count: parsed.derogs.late30 };
            newDerogs[bureau].late30 = { total: late.count || 0, to_remove: 0, age_bucket: late.age_bucket || "13-24" };
          }
          if (parsed.derogs.late60) {
            const late = typeof parsed.derogs.late60 === 'object' ? parsed.derogs.late60 : { count: parsed.derogs.late60 };
            newDerogs[bureau].late60 = { total: late.count || 0, to_remove: 0, age_bucket: late.age_bucket || "13-24" };
          }
          if (parsed.derogs.late90plus) {
            const late = typeof parsed.derogs.late90plus === 'object' ? parsed.derogs.late90plus : { count: parsed.derogs.late90plus };
            newDerogs[bureau].late90plus = { total: late.count || 0, to_remove: 0, age_bucket: late.age_bucket || "13-24" };
          }
          // Auto repo with deficiency tracking
          if (parsed.derogs.auto_repo) {
            const repo = typeof parsed.derogs.auto_repo === 'object' ? parsed.derogs.auto_repo : { count: parsed.derogs.auto_repo };
            newDerogs[bureau].auto_repo = { 
              total: repo.count || 0, 
              to_remove: 0,
              age_bucket: repo.age_bucket || "13-24",
              has_deficiency: repo.has_deficiency || false,
            };
          }
          // Mortgage foreclosure
          if (parsed.derogs.mortgage_foreclosure) {
            const fc = typeof parsed.derogs.mortgage_foreclosure === 'object' ? parsed.derogs.mortgage_foreclosure : { count: parsed.derogs.mortgage_foreclosure };
            newDerogs[bureau].mortgage_foreclosure = { 
              total: fc.count || 0, 
              to_remove: 0,
              age_bucket: fc.age_bucket || "13-24",
            };
          }
          // Student loans
          if (parsed.derogs.student_loans) {
            const sl = typeof parsed.derogs.student_loans === 'object' ? parsed.derogs.student_loans : { count: parsed.derogs.student_loans };
            newDerogs[bureau].student_loans = { 
              total: sl.count || 0, 
              to_remove: 0,
              delinquent: sl.delinquent || false,
            };
          }
        });
        
        setDerogs(newDerogs);
      }
      
      // Apply credit cards
      if (parsed.credit_cards && parsed.credit_cards.length > 0) {
        const newCards = parsed.credit_cards.map((card, idx) => ({
          id: Date.now() + idx,
          name: card.name || `Credit Card ${idx + 1}`,
          credit_limit: card.limit || 0,
          current_balance: card.balance || 0,
          target_balance: Math.round((card.limit || 0) * 0.1), // Default to 10% target
        }));
        setCreditCards(newCards);
      }
      
      // Apply authorized users
      if (parsed.authorized_users && parsed.authorized_users.length > 0) {
        const newAUs = parsed.authorized_users.map((au, idx) => ({
          id: Date.now() + idx + 100,
          card_name: `AU Tradeline ${idx + 1}`,
          au_age_bucket: au.age_years >= 10 ? "10+" : au.age_years >= 5 ? "5-10" : au.age_years >= 2 ? "2-5" : "<2",
          au_utilization_bucket: au.utilization < 10 ? "<10" : au.utilization < 30 ? "10-30" : au.utilization < 70 ? "30-70" : "70+",
          au_has_lates: au.has_lates || false,
          au_history_years: au.age_years || 5,
          au_credit_limit: au.limit || 10000,
          au_utilization: au.utilization || 5,
          au_is_clean: !au.has_lates,
        }));
        setAuthorizedUsers(newAUs);
      }
      
      // Store the analysis with action plan and recommendations
      setAgentAnalysis(parsed);
      
      toast.success("Scenario analyzed! Review the action plan below.");
      
    } catch (error) {
      console.error("AI Agent error:", error);
      
      // Fallback: Try to parse locally with regex
      const localParsed = parseScenarioLocally(inputText);
      if (localParsed) {
        applyLocalParsedData(localParsed);
        setAgentAnalysis(localParsed);
        toast.success("Scenario parsed successfully!");
      } else {
        toast.error("Failed to parse scenario. Please try again or use manual input.");
      }
    } finally {
      setAgentLoading(false);
    }
  };
  
  // Local parsing fallback (regex-based)
  const parseScenarioLocally = (text) => {
    const lowText = text.toLowerCase();
    const parsed = {
      scores: {},
      derogs: {},
      credit_cards: [],
      recommendations: [],
    };
    
    // Parse scores
    const equifaxMatch = lowText.match(/equifax[:\s]*(\d{3})/i) || lowText.match(/eq[:\s]*(\d{3})/i);
    const experianMatch = lowText.match(/experian[:\s]*(\d{3})/i) || lowText.match(/ex[:\s]*(\d{3})/i);
    const transunionMatch = lowText.match(/transunion[:\s]*(\d{3})/i) || lowText.match(/tu[:\s]*(\d{3})/i);
    
    if (equifaxMatch) parsed.scores.equifax = parseInt(equifaxMatch[1]);
    if (experianMatch) parsed.scores.experian = parseInt(experianMatch[1]);
    if (transunionMatch) parsed.scores.transunion = parseInt(transunionMatch[1]);
    
    // Parse derogs
    const collectionsMatch = lowText.match(/(\d+)\s*collection/i);
    const chargeoffsMatch = lowText.match(/(\d+)\s*charge\s*off/i);
    const repoMatch = lowText.match(/(\d+)\s*repo/i);
    const foreclosureMatch = lowText.match(/(\d+)\s*foreclosure/i);
    const late30Match = lowText.match(/(\d+)\s*30[\s-]*day\s*late/i);
    const late60Match = lowText.match(/(\d+)\s*60[\s-]*day\s*late/i);
    const late90Match = lowText.match(/(\d+)\s*90[\s-]*day\s*late/i);
    
    if (collectionsMatch) parsed.derogs.collections = parseInt(collectionsMatch[1]);
    if (chargeoffsMatch) parsed.derogs.chargeoffs = parseInt(chargeoffsMatch[1]);
    if (repoMatch) parsed.derogs.auto_repo = parseInt(repoMatch[1]);
    if (foreclosureMatch) parsed.derogs.mortgage_foreclosure = parseInt(foreclosureMatch[1]);
    if (late30Match) parsed.derogs.late30 = parseInt(late30Match[1]);
    if (late60Match) parsed.derogs.late60 = parseInt(late60Match[1]);
    if (late90Match) parsed.derogs.late90plus = parseInt(late90Match[1]);
    
    // Parse credit cards - look for patterns like "$700 limit $500 balance" or "limit of $700 balance of $500"
    const cardPatterns = [
      /\$?([\d,]+)\s*limit[^\d]*\$?([\d,]+)\s*balance/gi,
      /credit\s*card[^\d]*\$?([\d,]+)[^\d]*limit[^\d]*\$?([\d,]+)/gi,
      /card[^\d]*\$?([\d,]+)\s*limit[^\d]*\$?([\d,]+)/gi,
    ];
    
    cardPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(lowText)) !== null) {
        const limit = parseInt(match[1].replace(/,/g, ''));
        const balance = parseInt(match[2].replace(/,/g, ''));
        if (limit > 0) {
          parsed.credit_cards.push({ limit, balance, name: `Credit Card` });
        }
      }
    });
    
    // Remove duplicates
    const uniqueCards = [];
    const seen = new Set();
    parsed.credit_cards.forEach(card => {
      const key = `${card.limit}-${card.balance}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueCards.push(card);
      }
    });
    parsed.credit_cards = uniqueCards;
    
    // Generate recommendations
    parsed.recommendations = generateRecommendations(parsed);
    
    return parsed;
  };
  
  // Apply locally parsed data
  const applyLocalParsedData = (parsed) => {
    if (parsed.scores) {
      const newScores = { ...currentScores };
      const newEnabled = { ...enabledBureaus };
      
      if (parsed.scores.equifax) {
        newScores.EQUIFAX = parsed.scores.equifax;
        newEnabled.EQUIFAX = true;
      }
      if (parsed.scores.experian) {
        newScores.EXPERIAN = parsed.scores.experian;
        newEnabled.EXPERIAN = true;
      }
      if (parsed.scores.transunion) {
        newScores.TRANSUNION = parsed.scores.transunion;
        newEnabled.TRANSUNION = true;
      }
      
      setCurrentScores(newScores);
      setEnabledBureaus(newEnabled);
    }
    
    if (parsed.derogs) {
      const newDerogs = { ...derogs };
      ['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].forEach(bureau => {
        Object.entries(parsed.derogs).forEach(([key, value]) => {
          if (newDerogs[bureau][key]) {
            newDerogs[bureau][key] = { total: value, to_remove: 0 };
          }
        });
      });
      setDerogs(newDerogs);
    }
    
    if (parsed.credit_cards && parsed.credit_cards.length > 0) {
      const newCards = parsed.credit_cards.map((card, idx) => ({
        id: Date.now() + idx,
        name: card.name || `Credit Card ${idx + 1}`,
        credit_limit: card.limit || 0,
        current_balance: card.balance || 0,
        target_balance: Math.round((card.limit || 0) * 0.1),
      }));
      setCreditCards(newCards);
    }
  };
  
  // Generate recommendations based on parsed scenario
  const generateRecommendations = (parsed) => {
    const recs = [];
    
    // Utilization recommendations
    if (parsed.credit_cards && parsed.credit_cards.length > 0) {
      const totalLimit = parsed.credit_cards.reduce((sum, c) => sum + c.limit, 0);
      const totalBalance = parsed.credit_cards.reduce((sum, c) => sum + c.balance, 0);
      const utilization = totalLimit > 0 ? Math.round((totalBalance / totalLimit) * 100) : 0;
      
      if (utilization > 30) {
        const targetBalance = Math.round(totalLimit * 0.1);
        const paydownAmount = totalBalance - targetBalance;
        const estimatedGain = utilization > 70 ? '40-70' : utilization > 50 ? '25-45' : '10-25';
        
        recs.push({
          priority: 1,
          action: `Pay down credit cards to 10% utilization`,
          details: `Current: ${utilization}% → Target: 10%. Pay down $${paydownAmount.toLocaleString()}`,
          impact: `+${estimatedGain} points`,
          type: 'utilization',
        });
      }
    }
    
    // Derogatory removal recommendations
    if (parsed.derogs) {
      if (parsed.derogs.collections > 0) {
        recs.push({
          priority: 2,
          action: `Remove ${parsed.derogs.collections} Collection(s)`,
          details: `Dispute or pay-for-delete each collection account`,
          impact: `+${15 + (parsed.derogs.collections * 20)}-${25 + (parsed.derogs.collections * 25)} points`,
          type: 'derog_removal',
        });
      }
      
      if (parsed.derogs.chargeoffs > 0) {
        recs.push({
          priority: 2,
          action: `Address ${parsed.derogs.chargeoffs} Charge-Off(s)`,
          details: `Negotiate pay-for-delete or dispute inaccuracies`,
          impact: `+${20 + (parsed.derogs.chargeoffs * 25)}-${35 + (parsed.derogs.chargeoffs * 30)} points`,
          type: 'derog_removal',
        });
      }
      
      if (parsed.derogs.auto_repo > 0) {
        recs.push({
          priority: 1,
          action: `Address Auto Repossession`,
          details: `Dispute if inaccurate or negotiate removal after paying deficiency`,
          impact: `+45-70 points`,
          type: 'derog_removal',
        });
      }
      
      if (parsed.derogs.late30 > 0 || parsed.derogs.late60 > 0 || parsed.derogs.late90plus > 0) {
        const totalLates = (parsed.derogs.late30 || 0) + (parsed.derogs.late60 || 0) + (parsed.derogs.late90plus || 0);
        recs.push({
          priority: 3,
          action: `Remove ${totalLates} Late Payment(s)`,
          details: `Send goodwill letters or dispute inaccuracies`,
          impact: `+${totalLates * 8}-${totalLates * 15} points`,
          type: 'derog_removal',
        });
      }
    }
    
    // Sort by priority
    recs.sort((a, b) => a.priority - b.priority);
    
    return recs;
  };

  // ═══════════════════════════════════════════════════════════════════
  // REALISTIC CREDIT SCORE SIMULATION ENGINE v3.0
  // With Charge-Off Aging, Repo/Foreclosure Recovery Curves,
  // and Derogatory Gravity (Mass Effect)
  // Target: ±30 points accuracy
  // ═══════════════════════════════════════════════════════════════════
  
  // SCORECARD TYPES - Determines which scoring rules apply
  const SCORECARD_TYPES = {
    CLEAN: 'clean',           // No major derogatories
    MINOR_DEROG: 'minor',     // Only 30-day lates
    MAJOR_DEROG: 'major',     // Collections, charge-offs, repos, foreclosures, 60+ lates
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // MODULE 2: CHARGE-OFF AGING AND BALANCE STATUS MODEL
  // ═══════════════════════════════════════════════════════════════════
  
  // CO Status Factors - reflects severity based on status
  const CO_STATUS_FACTORS = {
    'with_balance': 1.00,      // CO with balance still reporting
    'settled': 0.80,           // CO settled for less than owed
    'paid': 0.65,              // CO paid in full
    'closed_unclear': 0.85,    // CO closed $0 but unclear if paid
  };
  
  // CO Recency Factors - newer COs hurt more
  const CO_RECENCY_FACTORS = {
    '0-6': 1.00,
    '7-12': 0.90,
    '13-24': 0.75,
    '25-48': 0.55,
    '49+': 0.35,
  };
  
  // CO Balance Factors - higher balances hurt more
  const CO_BALANCE_FACTORS = {
    'zero': 0.55,          // $0 balance
    'low': 0.70,           // $1-$999
    'medium': 0.85,        // $1,000-$4,999
    'high': 1.00,          // $5,000+
  };
  
  // CO Count Factors - diminishing impact per CO
  const CO_COUNT_FACTORS = {
    1: 1.00,
    2: 0.85,
    3: 0.70,
    4: 0.60,   // 4+
  };
  
  // Get CO recency factor based on months
  const getCORecencyFactor = (ageMonths) => {
    if (ageMonths <= 6) return CO_RECENCY_FACTORS['0-6'];
    if (ageMonths <= 12) return CO_RECENCY_FACTORS['7-12'];
    if (ageMonths <= 24) return CO_RECENCY_FACTORS['13-24'];
    if (ageMonths <= 48) return CO_RECENCY_FACTORS['25-48'];
    return CO_RECENCY_FACTORS['49+'];
  };
  
  // Get CO balance factor
  const getCOBalanceFactor = (balance) => {
    if (balance <= 0) return CO_BALANCE_FACTORS['zero'];
    if (balance < 1000) return CO_BALANCE_FACTORS['low'];
    if (balance < 5000) return CO_BALANCE_FACTORS['medium'];
    return CO_BALANCE_FACTORS['high'];
  };
  
  // Get CO count factor
  const getCOCountFactor = (count) => {
    if (count <= 1) return CO_COUNT_FACTORS[1];
    if (count === 2) return CO_COUNT_FACTORS[2];
    if (count === 3) return CO_COUNT_FACTORS[3];
    return CO_COUNT_FACTORS[4];
  };
  
  // Calculate CO Severity Score (per charge-off)
  const calculateCOSeverity = (status, ageMonths, balance, totalCOCount, isThickFile) => {
    const statusFactor = CO_STATUS_FACTORS[status] || 1.0;
    const recencyFactor = getCORecencyFactor(ageMonths);
    const balanceFactor = getCOBalanceFactor(balance);
    const countFactor = getCOCountFactor(totalCOCount);
    
    const severity = statusFactor * recencyFactor * balanceFactor * countFactor;
    
    // Base penalty points: 22 (old/thick) to 48 (recent/thin)
    const basePenalty = isThickFile ? 22 : 48;
    const adjustedBase = basePenalty - ((48 - 22) * (1 - recencyFactor));
    
    return {
      severity,
      penaltyPoints: Math.round(adjustedBase * severity),
      factors: { statusFactor, recencyFactor, balanceFactor, countFactor }
    };
  };
  
  // CO Event Lifts (when simulating improvements)
  const CO_EVENT_LIFTS = {
    balance_to_zero: { min: 5, max: 18 },      // Paying down CO balance to $0
    status_to_paid: { min: 8, max: 22 },       // CO updated to "Paid" or "Settled"
    deletion: { min: 18, max: 45 },            // CO deleted entirely
  };
  
  // Calculate lift from CO improvement event
  const calculateCOEventLift = (eventType, ageMonths, isThickFile) => {
    const lift = CO_EVENT_LIFTS[eventType];
    if (!lift) return 0;
    
    const recencyFactor = getCORecencyFactor(ageMonths);
    const thicknessMod = isThickFile ? 0.7 : 1.0;
    
    // Scale lift based on recency and file thickness
    const scaledLift = lift.min + (lift.max - lift.min) * recencyFactor * thicknessMod;
    return Math.round(scaledLift);
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // MODULE 3: REPO-FORECLOSURE RECOVERY CURVE + DEROGATORY GRAVITY
  // ═══════════════════════════════════════════════════════════════════
  
  // Repo Recovery Curve (penalty by age in months)
  const REPO_RECOVERY_CURVE = {
    '0-6': { min: 110, max: 160 },
    '7-12': { min: 90, max: 135 },
    '13-24': { min: 70, max: 115 },
    '25-48': { min: 45, max: 85 },
    '49-72': { min: 25, max: 55 },
    '73+': { min: 10, max: 30 },
  };
  
  // Foreclosure Recovery Curve (penalty by age in months)
  const FORECLOSURE_RECOVERY_CURVE = {
    '0-6': { min: 130, max: 190 },
    '7-12': { min: 110, max: 160 },
    '13-24': { min: 90, max: 140 },
    '25-48': { min: 60, max: 105 },
    '49-72': { min: 35, max: 75 },
    '73+': { min: 15, max: 40 },
  };
  
  // Get age bucket key
  const getAgeBucket = (ageMonths) => {
    if (ageMonths <= 6) return '0-6';
    if (ageMonths <= 12) return '7-12';
    if (ageMonths <= 24) return '13-24';
    if (ageMonths <= 48) return '25-48';
    if (ageMonths <= 72) return '49-72';
    return '73+';
  };
  
  // Calculate repo/foreclosure penalty based on recovery curve
  const calculateRepoForeclosurePenalty = (eventType, ageMonths, isThickFile, hasDeficiencyBalance, hasRelatedCollection) => {
    const curve = eventType === 'foreclosure' ? FORECLOSURE_RECOVERY_CURVE : REPO_RECOVERY_CURVE;
    const bucket = getAgeBucket(ageMonths);
    const range = curve[bucket];
    
    // Thin files get higher end of penalty
    let basePenalty = isThickFile ? range.min : range.max;
    
    // Deficiency balance multiplier (1.15 to 1.30)
    if (hasDeficiencyBalance) {
      basePenalty *= isThickFile ? 1.15 : 1.30;
    }
    
    // Related collection multiplier (1.10 to 1.25)
    if (hasRelatedCollection) {
      basePenalty *= isThickFile ? 1.10 : 1.25;
    }
    
    return Math.round(basePenalty);
  };
  
  // DEROGATORY GRAVITY (Mass Effect) - suppresses score improvements
  // When multiple major derogs exist, positive changes are reduced
  const DEROGATORY_GRAVITY_FACTORS = {
    0: 1.00,   // Clean file - full benefit from improvements
    1: 0.85,   // 1 major derog - 85% of improvement benefit
    2: 0.72,   // 2 major derogs - 72% of benefit
    3: 0.62,   // 3 major derogs - 62% of benefit
    4: 0.52,   // 4+ major derogs - 52% of benefit
  };
  
  // Calculate derogatory gravity factor
  const getDerogGravityFactor = (majorDerogCount) => {
    if (majorDerogCount <= 0) return DEROGATORY_GRAVITY_FACTORS[0];
    if (majorDerogCount === 1) return DEROGATORY_GRAVITY_FACTORS[1];
    if (majorDerogCount === 2) return DEROGATORY_GRAVITY_FACTORS[2];
    if (majorDerogCount === 3) return DEROGATORY_GRAVITY_FACTORS[3];
    return DEROGATORY_GRAVITY_FACTORS[4];
  };
  
  // Count major derogatories for gravity calculation - SIMPLIFIED
  const countMajorDerogs = (bureauDerogs) => {
    const majorTypes = ['collections', 'chargeoffs', 'auto_repo', 'mortgage_foreclosure', 'late90plus'];
    
    let count = 0;
    Object.entries(bureauDerogs).forEach(([key, item]) => {
      const remaining = (item.total || 0) - (item.to_remove || 0);
      if (remaining > 0 && majorTypes.includes(key)) {
        count += remaining;
      }
    });
    return count;
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // ENHANCED SCORE CEILINGS (with CO + Collection combos)
  // ═══════════════════════════════════════════════════════════════════
  
  const SCORE_CEILINGS = {
    // Standard dirty file ceilings
    dirty_high_util: { min: 640, max: 690 },
    dirty_moderate_util: { min: 660, max: 710 },
    dirty_low_util: { min: 680, max: 730 },
    // Clean file ceilings
    clean_high_util: { min: 680, max: 720 },
    clean_moderate_util: { min: 700, max: 760 },
    clean_optimal_util: { min: 740, max: 830 },
    clean_low_util: { min: 720, max: 790 },
    // Special condition ceilings (Module 2 guardrails)
    co_remaining_high_util: { min: 630, max: 690 },    // CO remains + util > 50%
    co_remaining_moderate_util: { min: 660, max: 710 }, // CO remains + util 30-50%
    co_and_collection: { min: 600, max: 660 },         // CO + Collection both remain
  };
  
  // DEROGATORY LABELS - SIMPLIFIED (no recent/old distinction)
  const DEROG_LABELS = {
    collections: "Collections",
    chargeoffs: "Charge-Offs",
    late30: "30-Day Late Payments",
    late60: "60-Day Late Payments",
    late90plus: "90+ Day Late Payments",
    student_loans: "Student Loans (Default)",
    auto_repo: "Auto Repossession",
    auto_closure_balance: "Auto Closure w/ Balance",
    mortgage_foreclosure: "Mortgage Foreclosure",
  };
  
  // BASE IMPACT PER DEROG TYPE - SIMPLIFIED
  const DEROG_BASE_IMPACT = {
    late30: 15,
    late60: 30,
    late90plus: 50,
    collections: 40,        // Combined collections impact
    chargeoffs: 55,         // Combined charge-offs impact
    student_loans: 40,
    auto_closure_balance: 45,
    auto_repo: 70,
    mortgage_foreclosure: 100,
  };
  
  // Determine scorecard type based on derogatory items - SIMPLIFIED
  const determineScorecard = (bureauDerogs) => {
    const majorDerogs = ['collections', 'chargeoffs', 'auto_repo', 'mortgage_foreclosure', 
      'student_loans', 'auto_closure_balance', 'late60', 'late90plus'];
    const minorDerogs = ['late30'];
    
    let hasMajor = false;
    let hasMinor = false;
    
    Object.entries(bureauDerogs).forEach(([key, item]) => {
      const remaining = (item.total || 0) - (item.to_remove || 0);
      if (remaining > 0) {
        if (majorDerogs.includes(key)) hasMajor = true;
        if (minorDerogs.includes(key)) hasMinor = true;
      }
    });
    
    if (hasMajor) return SCORECARD_TYPES.MAJOR_DEROG;
    if (hasMinor) return SCORECARD_TYPES.MINOR_DEROG;
    return SCORECARD_TYPES.CLEAN;
  };
  
  // Get utilization penalty based on percentage
  const getUtilizationPenalty = (utilPercent) => {
    if (utilPercent <= 9) return 0;
    if (utilPercent <= 29) return -20;
    if (utilPercent <= 49) return -45;
    if (utilPercent <= 74) return -70;
    if (utilPercent <= 89) return -95;
    return -120;
  };
  
  // Calculate individual card utilization penalty (single maxed card hurts more)
  const getMaxCardUtilizationPenalty = (cards) => {
    if (!cards || cards.length === 0) return 0;
    
    let maxUtil = 0;
    cards.forEach(card => {
      if (card.credit_limit > 0) {
        const util = (card.current_balance / card.credit_limit) * 100;
        maxUtil = Math.max(maxUtil, util);
      }
    });
    
    // Additional penalty for having a maxed card
    if (maxUtil >= 90) return -25;
    if (maxUtil >= 75) return -15;
    if (maxUtil >= 50) return -8;
    return 0;
  };
  
  // Calculate cards with balances penalty
  const getCardsWithBalancesPenalty = (cards) => {
    if (!cards || cards.length === 0) return 0;
    
    const cardsWithBalance = cards.filter(c => c.current_balance > 0).length;
    const totalCards = cards.length;
    
    if (totalCards === 0) return 0;
    
    const ratio = cardsWithBalance / totalCards;
    if (ratio >= 0.9) return -15; // All cards have balances
    if (ratio >= 0.7) return -10; // Most cards have balances
    if (ratio >= 0.5) return -5;  // Half have balances
    return 0;
  };
  
  // Apply diminishing returns - each subsequent item has less impact
  const applyDiminishingReturns = (count, baseImpact) => {
    let total = 0;
    for (let i = 0; i < count; i++) {
      // Each subsequent item gives 55% of the previous impact (more aggressive decay)
      total += baseImpact * Math.pow(0.55, i);
    }
    return Math.round(total);
  };
  
  // Calculate derogatory penalty with frequency factor
  const calculateDerogPenalty = (bureauDerogs, includeRemoved = false) => {
    let totalPenalty = 0;
    let derogCount = 0;
    
    Object.entries(bureauDerogs).forEach(([key, item]) => {
      const count = includeRemoved ? (item.total || 0) : ((item.total || 0) - (item.to_remove || 0));
      if (count > 0 && DEROG_BASE_IMPACT[key]) {
        const basePenalty = applyDiminishingReturns(count, DEROG_BASE_IMPACT[key]);
        totalPenalty += basePenalty;
        derogCount += count;
      }
    });
    
    // Frequency multiplier - multiple derogs compound the penalty
    let frequencyMultiplier = 1.0;
    if (derogCount >= 7) frequencyMultiplier = 1.3;
    else if (derogCount >= 5) frequencyMultiplier = 1.2;
    else if (derogCount >= 3) frequencyMultiplier = 1.1;
    
    return Math.round(totalPenalty * frequencyMultiplier);
  };
  
  // Get score ceiling based on file condition with Module 2 guardrails - SIMPLIFIED
  const getScoreCeiling = (scorecard, utilizationPercent, bureauDerogs) => {
    const isDirty = scorecard === SCORECARD_TYPES.MAJOR_DEROG;
    
    // Check for CO + Collection combination (Module 2 guardrail)
    const hasChargeoff = ((bureauDerogs.chargeoffs?.total || 0) - (bureauDerogs.chargeoffs?.to_remove || 0)) > 0;
    const hasCollection = ((bureauDerogs.collections?.total || 0) - (bureauDerogs.collections?.to_remove || 0)) > 0;
    
    // If both CO and Collection remain, apply strictest ceiling
    if (hasChargeoff && hasCollection) {
      return SCORE_CEILINGS.co_and_collection;
    }
    
    // If CO remains with high utilization
    if (hasChargeoff && utilizationPercent > 50) {
      return SCORE_CEILINGS.co_remaining_high_util;
    }
    
    // If CO remains with moderate utilization
    if (hasChargeoff && utilizationPercent > 30) {
      return SCORE_CEILINGS.co_remaining_moderate_util;
    }
    
    if (isDirty) {
      if (utilizationPercent > 50) return SCORE_CEILINGS.dirty_high_util;
      if (utilizationPercent > 30) return SCORE_CEILINGS.dirty_moderate_util;
      return SCORE_CEILINGS.dirty_low_util;
    } else {
      if (utilizationPercent > 70) return SCORE_CEILINGS.clean_high_util;
      if (utilizationPercent > 30) return SCORE_CEILINGS.clean_moderate_util;
      if (utilizationPercent > 10) return SCORE_CEILINGS.clean_low_util;
      return SCORE_CEILINGS.clean_optimal_util;
    }
  };
  
  // Credit file depth assessment
  const assessFileDepth = (avgAgeYears, accountCount) => {
    const isThin = accountCount < 3 || avgAgeYears < 2;
    const isThick = accountCount >= 4 && avgAgeYears >= 3;
    
    return {
      isThin,
      isThick,
      depthPenalty: isThin ? -25 : 0,
      depthBonus: isThick ? 10 : 0,
    };
  };
  
  // Account age factor
  const calculateAgeFactor = (avgAgeYears, oldestAgeYears) => {
    let ageBonus = 0;
    let agePenalty = 0;
    
    // Young average age penalty
    if (avgAgeYears && avgAgeYears < 2) agePenalty = -20;
    else if (avgAgeYears && avgAgeYears < 3) agePenalty = -10;
    
    // Mature file bonus
    if (oldestAgeYears && oldestAgeYears >= 10) ageBonus = 15;
    else if (oldestAgeYears && oldestAgeYears >= 7) ageBonus = 10;
    else if (oldestAgeYears && oldestAgeYears >= 5) ageBonus = 5;
    
    return ageBonus + agePenalty;
  };
  
  // Authorized User impact (limited to 30-60% of primary account impact)
  const calculateAUImpact = (au, index) => {
    if (!au) return 0;
    
    // Base impact from AU
    let baseImpact = 0;
    
    // History contribution (30-60% of primary impact)
    const historyYears = au.au_history_years || 0;
    if (historyYears >= 10) baseImpact = 25;
    else if (historyYears >= 7) baseImpact = 20;
    else if (historyYears >= 5) baseImpact = 15;
    else if (historyYears >= 3) baseImpact = 10;
    else if (historyYears >= 1) baseImpact = 5;
    
    // Limit contribution (based on credit limit)
    const limitBonus = au.au_credit_limit >= 10000 ? 8 : 
                       au.au_credit_limit >= 5000 ? 5 : 
                       au.au_credit_limit >= 2000 ? 3 : 0;
    
    // Clean history bonus
    const cleanBonus = au.au_is_clean ? 5 : -10;
    
    // High utilization on AU reduces benefit
    const utilPenalty = au.au_utilization > 30 ? -Math.min(au.au_utilization / 5, 15) : 0;
    
    // Diminishing returns for multiple AUs
    const diminishingFactor = Math.pow(0.6, index);
    
    const totalImpact = (baseImpact + limitBonus + cleanBonus + utilPenalty) * diminishingFactor;
    return Math.round(Math.max(0, totalImpact));
  };
  
  // Inquiry penalty
  const calculateInquiryPenalty = (inquiryCount) => {
    if (inquiryCount <= 2) return 0;
    if (inquiryCount <= 5) return -Math.min((inquiryCount - 2) * 5, 15);
    return -Math.min(15 + (inquiryCount - 5) * 3, 30);
  };
  
  // MAIN SCORE CALCULATION ENGINE v3.0
  // With Derogatory Gravity (Mass Effect) and Module 2/3 integration
  const calculateProjectedScore = (currentScore, bureauDerogs, utilCurrent, utilTarget, 
    cards, authorizedUsers, fileData, includeRemovals = true) => {
    
    // 1. Determine scorecard AFTER removals
    const scorecardBefore = determineScorecard(bureauDerogs);
    const scorecardAfter = includeRemovals ? determineScorecard(bureauDerogs) : scorecardBefore;
    
    // 2. Calculate current derog penalty (before removals)
    const currentDerogPenalty = calculateDerogPenalty(bureauDerogs, true);
    
    // 3. Calculate projected derog penalty (after removals)
    const projectedDerogPenalty = calculateDerogPenalty(bureauDerogs, false);
    
    // 4. Derog removal benefit (before gravity)
    const rawDerogRemovalBenefit = currentDerogPenalty - projectedDerogPenalty;
    
    // 5. Count remaining major derogs for gravity calculation
    const remainingMajorDerogCount = countMajorDerogs(bureauDerogs);
    const gravityFactor = getDerogGravityFactor(remainingMajorDerogCount);
    
    // 6. Apply derogatory gravity to ALL positive changes
    const derogRemovalBenefit = Math.round(rawDerogRemovalBenefit * gravityFactor);
    
    // 7. Current utilization penalty
    const currentUtilPenalty = getUtilizationPenalty(utilCurrent);
    const targetUtilPenalty = getUtilizationPenalty(utilTarget);
    const rawUtilizationBenefit = currentUtilPenalty - targetUtilPenalty;
    const utilizationBenefit = Math.round(rawUtilizationBenefit * gravityFactor);
    
    // 8. Individual card and cards-with-balance penalties
    const maxCardPenalty = getMaxCardUtilizationPenalty(cards);
    const cardsBalancePenalty = getCardsWithBalancesPenalty(cards);
    
    // 9. File depth assessment
    const fileDepth = assessFileDepth(fileData.avg_age_years, fileData.revolving_accounts_count);
    
    // 10. Age factor
    const ageFactor = calculateAgeFactor(fileData.avg_age_years, fileData.oldest_age_years);
    
    // 11. AU impact (also affected by gravity)
    const rawAuImpact = authorizedUsers.reduce((sum, au, idx) => sum + calculateAUImpact(au, idx), 0);
    const auImpact = Math.round(rawAuImpact * gravityFactor);
    
    // 12. Inquiry penalty
    const inquiryPenalty = calculateInquiryPenalty(fileData.inquiries_0_12mo || 0);
    
    // 13. Calculate raw projected score
    let projectedScore = currentScore 
      + derogRemovalBenefit 
      + utilizationBenefit
      + (fileDepth.depthBonus * gravityFactor) + fileDepth.depthPenalty  // Bonus affected by gravity
      + (ageFactor > 0 ? ageFactor * gravityFactor : ageFactor)  // Only positive affected
      + auImpact
      + inquiryPenalty;
    
    // 14. Apply remaining derog penalty to projection
    projectedScore -= projectedDerogPenalty * 0.15;
    
    // 15. Apply score ceiling based on file condition (with CO+Collection guardrails)
    const ceiling = getScoreCeiling(scorecardAfter, utilTarget, bureauDerogs);
    
    // Apply ceiling - projected score cannot exceed max for file condition
    const cappedScore = Math.min(projectedScore, ceiling.max);
    
    // Apply floor
    const finalScore = Math.max(cappedScore, 300);
    
    // Calculate range (±15 points)
    const projectedLow = Math.max(300, finalScore - 15);
    const projectedHigh = Math.min(ceiling.max, finalScore + 15);
    
    // Identify top 3 factors holding score down
    const holdingFactors = [];
    
    // Check utilization
    if (utilTarget > 50) {
      holdingFactors.push({ factor: 'High Utilization', impact: -Math.abs(targetUtilPenalty), severity: 'critical' });
    } else if (utilTarget > 30) {
      holdingFactors.push({ factor: 'Moderate Utilization', impact: -Math.abs(targetUtilPenalty), severity: 'moderate' });
    }
    
    // Check remaining derogs
    if (remainingMajorDerogCount > 0) {
      const hasRecentCO = ((bureauDerogs.chargeoffs?.total || 0) - (bureauDerogs.chargeoffs?.to_remove || 0)) > 0;
      const hasRepo = ((bureauDerogs.auto_repo?.total || 0) - (bureauDerogs.auto_repo?.to_remove || 0)) > 0;
      const hasForeclosure = ((bureauDerogs.mortgage_foreclosure?.total || 0) - (bureauDerogs.mortgage_foreclosure?.to_remove || 0)) > 0;
      const hasCollections = ((bureauDerogs.collections?.total || 0) - (bureauDerogs.collections?.to_remove || 0)) > 0;
      
      if (hasForeclosure) {
        holdingFactors.push({ factor: 'Mortgage Foreclosure', impact: -100, severity: 'critical' });
      }
      if (hasRepo) {
        holdingFactors.push({ factor: 'Auto Repossession', impact: -70, severity: 'critical' });
      }
      if (hasRecentCO) {
        holdingFactors.push({ factor: 'Charge-Off', impact: -55, severity: 'critical' });
      }
      if (hasCollections) {
        holdingFactors.push({ factor: 'Collections', impact: -40, severity: 'high' });
      }
    }
    
    // Check file depth
    if (fileDepth.isThin) {
      holdingFactors.push({ factor: 'Thin Credit File', impact: fileDepth.depthPenalty, severity: 'moderate' });
    }
    
    // Check inquiries
    if ((fileData.inquiries_0_12mo || 0) > 5) {
      holdingFactors.push({ factor: 'Too Many Inquiries', impact: inquiryPenalty, severity: 'moderate' });
    }
    
    // Sort by impact severity and take top 3
    holdingFactors.sort((a, b) => a.impact - b.impact);
    const topHoldingFactors = holdingFactors.slice(0, 3);
    
    return {
      projectedScore: Math.round(finalScore),
      projectedLow: Math.round(projectedLow),
      projectedHigh: Math.round(projectedHigh),
      scorecard: scorecardAfter,
      scorecardLabel: scorecardAfter === SCORECARD_TYPES.CLEAN ? 'Clean' : 
                      scorecardAfter === SCORECARD_TYPES.MINOR_DEROG ? 'Dirty Minor' : 'Dirty Major',
      ceiling,
      gravityFactor,
      remainingMajorDerogCount,
      topHoldingFactors,
      breakdown: {
        currentScore,
        derogRemovalBenefit,
        rawDerogRemovalBenefit,
        utilizationBenefit,
        rawUtilizationBenefit,
        maxCardPenalty,
        cardsBalancePenalty,
        fileDepthBonus: fileDepth.depthBonus,
        fileDepthPenalty: fileDepth.depthPenalty,
        ageFactor,
        auImpact,
        rawAuImpact,
        inquiryPenalty,
        remainingDerogPenalty: Math.round(projectedDerogPenalty * 0.15),
        gravityFactor,
        ceilingApplied: projectedScore > ceiling.max,
      }
    };
  };
  
  // Calculate derog removal breakdown for display
  const calculateDerogBreakdown = (bureauDerogs) => {
    const breakdown = [];
    
    Object.entries(bureauDerogs).forEach(([key, item]) => {
      const toRemove = item.to_remove || 0;
      const total = item.total || 0;
      const remaining = total - toRemove;
      
      if (toRemove > 0 && DEROG_BASE_IMPACT[key]) {
        const removalImpact = applyDiminishingReturns(toRemove, DEROG_BASE_IMPACT[key]);
        
        breakdown.push({
          type: key,
          label: DEROG_LABELS[key],
          total: total,
          to_remove: toRemove,
          remaining: remaining,
          impact_per_item: DEROG_BASE_IMPACT[key],
          removal_impact: removalImpact,
          remaining_penalty: remaining > 0 ? Math.round(applyDiminishingReturns(remaining, DEROG_BASE_IMPACT[key]) * 0.15) : 0,
          total_impact: removalImpact,
        });
      }
    });
    
    return breakdown;
  };

  // ═══════════════════════════════════════════════════════════════════
  // SIMULATION - Uses new realistic scoring engine
  // ═══════════════════════════════════════════════════════════════════
  
  const runSimulation = async () => {
    // Check if at least one bureau is enabled
    const activeBureaus = getEnabledBureaus();
    if (activeBureaus.length === 0) {
      toast.error("Please enable at least one bureau to simulate");
      return;
    }
    
    setSimulating(true);
    
    try {
      // Calculate utilization from cards
      const utilCurrent = getTotalUtilization(false);
      const utilTarget = getTotalUtilization(true);
      
      // Build results for ENABLED bureaus only
      const bureauResults = {};
      const derogBreakdowns = {};
      const impactBreakdowns = {};
      
      for (const bureau of activeBureaus) {
        // Calculate derog breakdown for this bureau
        derogBreakdowns[bureau] = calculateDerogBreakdown(derogs[bureau]);
        
        // Use NEW scoring engine for projection
        const projection = calculateProjectedScore(
          currentScores[bureau],
          derogs[bureau],
          utilCurrent,
          utilTarget,
          creditCards,
          authorizedUsers,
          fileData,
          true // include removals
        );
        
        bureauResults[bureau] = {
          current_score: currentScores[bureau],
          projected_expected: projection.projectedScore,
          projected_low: projection.projectedLow,
          projected_high: projection.projectedHigh,
          delta_expected: projection.projectedScore - currentScores[bureau],
          scorecard: projection.scorecard,
          scorecardLabel: projection.scorecardLabel,
          ceiling: projection.ceiling,
          gravityFactor: projection.gravityFactor,
          remainingMajorDerogCount: projection.remainingMajorDerogCount,
          topHoldingFactors: projection.topHoldingFactors,
          breakdown: projection.breakdown,
        };
        
        // Build detailed impact breakdown for display
        impactBreakdowns[bureau] = {
          starting_score: currentScores[bureau],
          derogatory_removal_impact: projection.breakdown.derogRemovalBenefit,
          raw_derog_removal_impact: projection.breakdown.rawDerogRemovalBenefit,
          remaining_derog_penalty: -projection.breakdown.remainingDerogPenalty,
          utilization_impact: projection.breakdown.utilizationBenefit,
          raw_utilization_impact: projection.breakdown.rawUtilizationBenefit,
          authorized_user_impact: projection.breakdown.auImpact,
          raw_au_impact: projection.breakdown.rawAuImpact,
          inquiry_impact: projection.breakdown.inquiryPenalty,
          age_factor: projection.breakdown.ageFactor,
          file_depth: projection.breakdown.fileDepthBonus + projection.breakdown.fileDepthPenalty,
          projected_score: projection.projectedScore,
          projected_low: projection.projectedLow,
          projected_high: projection.projectedHigh,
          total_change: projection.projectedScore - currentScores[bureau],
          ceiling_applied: projection.breakdown.ceilingApplied,
          max_achievable: projection.ceiling.max,
          gravity_factor: projection.gravityFactor,
          gravity_applied: projection.gravityFactor < 1.0,
          top_holding_factors: projection.topHoldingFactors,
        };
      }
      
      // Add card-specific breakdown using new utilization model
      const cardBreakdowns = creditCards.map(card => {
        const currentUtil = calculateUtilization(card.current_balance, card.credit_limit);
        const targetUtil = calculateUtilization(card.target_balance, card.credit_limit);
        const paydown = card.current_balance - card.target_balance;
        
        // Use new utilization penalty function
        const currentPenalty = getUtilizationPenalty(currentUtil);
        const targetPenalty = getUtilizationPenalty(targetUtil);
        
        // Impact is improvement from current to target (positive = better)
        let impact = currentPenalty - targetPenalty;
        
        // Additional bonus for crossing major thresholds (nonlinear benefit)
        if (currentUtil > 74 && targetUtil <= 29) impact += 20;
        else if (currentUtil > 49 && targetUtil <= 9) impact += 15;
        else if (currentUtil > 29 && targetUtil <= 9) impact += 10;
        
        // Calculate 10% optimal scenario
        const optimal10Balance = Math.round(card.credit_limit * 0.1);
        const optimal10Util = 10;
        const paydownTo10 = Math.max(0, card.current_balance - optimal10Balance);
        
        // Calculate impact if paid down to 10%
        const impact10Percent = currentPenalty - getUtilizationPenalty(10);
        
        return {
          name: card.name || "Credit Card",
          credit_limit: card.credit_limit,
          current_balance: card.current_balance,
          target_balance: card.target_balance,
          paydown_amount: paydown,
          current_utilization: currentUtil,
          target_utilization: targetUtil,
          current_penalty: currentPenalty,
          target_penalty: targetPenalty,
          estimated_impact: impact,
          optimal_10_balance: optimal10Balance,
          optimal_10_utilization: optimal10Util,
          paydown_to_10: paydownTo10,
          impact_at_10_percent: impact10Percent,
        };
      });
      
      // Add AU breakdown
      const auBreakdowns = authorizedUsers.map((au, idx) => ({
        card_name: au.card_name || `AU Tradeline #${idx + 1}`,
        history_years: au.au_history_years,
        credit_limit: au.au_credit_limit,
        utilization: au.au_utilization,
        is_clean: au.au_is_clean,
        estimated_impact: calculateAUImpact(au, idx),
      }));
      
      // Calculate 10% utilization scenario using new engine
      let bureauResults10Percent = {};
      let impactBreakdowns10Percent = {};
      
      if (creditCards.length > 0) {
        for (const bureau of activeBureaus) {
          // Use NEW scoring engine with 10% utilization
          const projection10 = calculateProjectedScore(
            currentScores[bureau],
            derogs[bureau],
            utilCurrent,
            10, // 10% target utilization
            creditCards,
            authorizedUsers,
            fileData,
            true
          );
          
          bureauResults10Percent[bureau] = {
            current_score: currentScores[bureau],
            projected_expected: projection10.projectedScore,
            projected_low: projection10.projectedLow,
            projected_high: projection10.projectedHigh,
            delta_expected: projection10.projectedScore - currentScores[bureau],
            scorecard: projection10.scorecard,
            ceiling: projection10.ceiling,
          };
          
          impactBreakdowns10Percent[bureau] = {
            starting_score: currentScores[bureau],
            utilization_impact: projection10.breakdown.utilizationBenefit,
            projected_score: projection10.projectedScore,
            total_change: projection10.projectedScore - currentScores[bureau],
            max_achievable: projection10.ceiling.max,
          };
        }
      }
      
      setResults({
        bureaus: bureauResults,
        derogBreakdowns: derogBreakdowns,
        impactBreakdowns: impactBreakdowns,
        enabledBureaus: activeBureaus,
        cards: cardBreakdowns,
        authorizedUsers: auBreakdowns,
        model: model,
        simulatedAt: new Date().toISOString(),
        // 10% utilization scenario
        bureaus10Percent: bureauResults10Percent,
        impactBreakdowns10Percent: impactBreakdowns10Percent,
        has10PercentProjection: creditCards.length > 0,
      });
      
      toast.success("Score simulation complete for all bureaus!");
    } catch (error) {
      console.error("Simulation error:", error);
      toast.error("Failed to run simulation");
    } finally {
      setSimulating(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // EMAIL RESULTS
  // ═══════════════════════════════════════════════════════════════════
  
  const sendEmail = async () => {
    if (!clientEmail) {
      toast.error("Please enter client email");
      return;
    }
    if (!results) {
      toast.error("Run simulation first");
      return;
    }
    
    setSendingEmail(true);
    try {
      // Only include enabled bureaus
      const enabledBureausList = results.enabledBureaus || BUREAUS;
      const enabledResults = Object.entries(results.bureaus)
        .filter(([bureau]) => enabledBureausList.includes(bureau));
      
      // Find best bureau from enabled ones
      const bestBureau = enabledResults.reduce((best, [bureau, data]) => {
        if (!best || data.projected_expected > best.data.projected_expected) {
          return { bureau, data };
        }
        return best;
      }, null);
      
      if (!bestBureau) {
        toast.error("No bureau results to send");
        return;
      }
      
      await axios.post(`${API}/prescreening/send-email`, {
        client_email: clientEmail,
        client_name: clientName || "Valued Client",
        simulation_result: {
          ...bestBureau.data,
          // Include range data for email
          projected_range_low: Math.max(300, bestBureau.data.projected_expected - 15),
          projected_range_high: Math.min(850, bestBureau.data.projected_expected + 15),
          delta_range_low: Math.max(0, bestBureau.data.delta_expected - 15),
          delta_range_high: bestBureau.data.delta_expected + 15,
          // Only send enabled bureaus with ranges
          all_bureaus: enabledResults.map(([b, d]) => ({
            bureau: b,
            current: d.current_score,
            projected_low: Math.max(300, d.projected_expected - 15),
            projected_high: Math.min(850, d.projected_expected + 15),
            delta_low: Math.max(0, d.delta_expected - 15),
            delta_high: d.delta_expected + 15,
          })),
          enabled_bureaus: enabledBureausList,
          // Include derogatory removal breakdown for the email (only enabled bureaus)
          derog_removal_breakdown: results.derogBreakdowns ? 
            Object.entries(results.derogBreakdowns)
              .filter(([bureau]) => enabledBureausList.includes(bureau))
              .flatMap(([bureau, items]) => 
                items.map(item => ({
                  bureau,
                  type: item.label,
                  count: item.to_remove || 0,
                  total: item.total || 0,
                  remaining: item.remaining || 0,
                  impact_per_item: item.impact_per_item,
                  total_impact: item.total_impact,
                }))
              ) : [],
          // Include impact breakdown for enabled bureaus
          impact_breakdown: results.impactBreakdowns ? 
            Object.entries(results.impactBreakdowns)
              .filter(([bureau]) => enabledBureausList.includes(bureau))
              .map(([bureau, breakdown]) => ({
                bureau,
                starting_score: breakdown.starting_score,
                derogatory_impact: breakdown.derogatory_removal_impact,
                utilization_impact: breakdown.utilization_impact,
                au_impact: breakdown.authorized_user_impact,
                inquiry_impact: breakdown.inquiry_impact,
                total_change_low: Math.max(0, breakdown.total_change - 15),
                total_change_high: breakdown.total_change + 15,
              })) : [],
          cards_breakdown: results.cards,
          au_breakdown: results.authorizedUsers,
        },
        include_pdf: true,
      });
      
      toast.success(`Results emailed to ${clientEmail}`);
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // EMAIL AI AGENT ANALYSIS (for AI Credit Agent mode)
  // ═══════════════════════════════════════════════════════════════════
  
  const handleEmail = async () => {
    if (!clientEmail) {
      toast.error("Please enter client email");
      return;
    }
    
    if (!agentAnalysis) {
      toast.error("Run AI analysis first");
      return;
    }
    
    setSendingEmail(true);
    try {
      // Build simulation result from AI analysis
      const scores = agentAnalysis.scores || {};
      const avgScore = Object.values(scores).length > 0 
        ? Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length)
        : 0;
      
      // Calculate utilization from credit cards
      const creditCardsData = agentAnalysis.credit_cards || [];
      const totalLimit = creditCardsData.reduce((sum, c) => sum + (c.limit || 0), 0);
      const totalBalance = creditCardsData.reduce((sum, c) => sum + (c.balance || 0), 0);
      const currentUtil = totalLimit > 0 ? Math.round((totalBalance / totalLimit) * 100) : 0;
      
      // Build bureau results
      const enabledBureausList = [];
      const allBureaus = [];
      
      if (scores.equifax) {
        enabledBureausList.push("EQUIFAX");
        allBureaus.push({
          bureau: "EQUIFAX",
          current: scores.equifax,
          projected_low: agentAnalysis.estimated_potential_low || scores.equifax + 30,
          projected_high: agentAnalysis.estimated_potential_high || scores.equifax + 60,
          delta_low: 30,
          delta_high: agentAnalysis.estimated_max_improvement || 60,
        });
      }
      if (scores.experian) {
        enabledBureausList.push("EXPERIAN");
        allBureaus.push({
          bureau: "EXPERIAN",
          current: scores.experian,
          projected_low: agentAnalysis.estimated_potential_low || scores.experian + 30,
          projected_high: agentAnalysis.estimated_potential_high || scores.experian + 60,
          delta_low: 30,
          delta_high: agentAnalysis.estimated_max_improvement || 60,
        });
      }
      if (scores.transunion) {
        enabledBureausList.push("TRANSUNION");
        allBureaus.push({
          bureau: "TRANSUNION",
          current: scores.transunion,
          projected_low: agentAnalysis.estimated_potential_low || scores.transunion + 30,
          projected_high: agentAnalysis.estimated_potential_high || scores.transunion + 60,
          delta_low: 30,
          delta_high: agentAnalysis.estimated_max_improvement || 60,
        });
      }
      
      // Build derogatory breakdown from AI analysis
      const derogBreakdown = [];
      const derogs = agentAnalysis.derogs || {};
      if (derogs.collections?.count > 0 || derogs.collections > 0) {
        const count = derogs.collections?.count || derogs.collections;
        derogBreakdown.push({
          type: "Collections",
          count: count,
          total_impact: count * 25,
        });
      }
      if (derogs.chargeoffs?.count > 0 || derogs.chargeoffs > 0) {
        const count = derogs.chargeoffs?.count || derogs.chargeoffs;
        derogBreakdown.push({
          type: "Charge-Offs",
          count: count,
          total_impact: count * 35,
        });
      }
      if (derogs.auto_repo?.count > 0 || derogs.auto_repo > 0) {
        const count = derogs.auto_repo?.count || derogs.auto_repo;
        derogBreakdown.push({
          type: "Auto Repossession",
          count: count,
          total_impact: count * 50,
        });
      }
      const totalLates = (derogs.late30?.count || derogs.late30 || 0) + 
                        (derogs.late60?.count || derogs.late60 || 0) + 
                        (derogs.late90plus?.count || derogs.late90plus || 0);
      if (totalLates > 0) {
        derogBreakdown.push({
          type: "Late Payments",
          count: totalLates,
          total_impact: totalLates * 12,
        });
      }
      
      // Build action plan summary for email
      const actionPlan = agentAnalysis.action_plan || [];
      const actionPlanSummary = actionPlan.slice(0, 5).map(a => ({
        step: a.step,
        action: a.action,
        impact: a.estimated_impact,
        timeline: a.timeline,
      }));
      
      await axios.post(`${API}/prescreening/send-email`, {
        client_email: clientEmail,
        client_name: clientName || "Valued Client",
        simulation_result: {
          current_score: avgScore,
          projected_expected: agentAnalysis.estimated_potential_high || avgScore + 50,
          projected_range_low: agentAnalysis.estimated_potential_low || avgScore + 20,
          projected_range_high: agentAnalysis.estimated_potential_high || avgScore + 70,
          delta_expected: agentAnalysis.estimated_max_improvement || 50,
          delta_range_low: 20,
          delta_range_high: agentAnalysis.estimated_max_improvement || 70,
          all_bureaus: allBureaus,
          enabled_bureaus: enabledBureausList,
          model: "BLEND",
          derog_removal_breakdown: derogBreakdown,
          // Include action plan in assumptions for the email
          assumptions: {
            ai_analysis: true,
            action_plan_steps: actionPlan.length,
            confidence_level: agentAnalysis.confidence_level || confidenceLevel,
            goal_mode: agentAnalysis.goal_mode || goalMode,
            this_week_focus: agentAnalysis.email_summary?.this_week,
            action_plan_summary: actionPlanSummary,
          },
          // Credit card breakdown
          cards_breakdown: creditCardsData.map((card, idx) => ({
            name: card.name || `Credit Card ${idx + 1}`,
            credit_limit: card.limit || 0,
            current_balance: card.balance || 0,
            target_balance: Math.round((card.limit || 0) * 0.1),
            current_utilization: card.limit > 0 ? Math.round((card.balance / card.limit) * 100) : 0,
            target_utilization: 10,
            paydown_amount: Math.max(0, (card.balance || 0) - Math.round((card.limit || 0) * 0.1)),
          })),
          // Cap warnings
          cap_warnings: agentAnalysis.cap_warnings || [],
          // Email summary from AI
          email_summary: agentAnalysis.email_summary,
        },
        include_pdf: true,
      });
      
      toast.success(`AI Analysis emailed to ${clientEmail}`);
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // RESET FORM
  // ═══════════════════════════════════════════════════════════════════
  
  const defaultDerogItem = { total: 0, to_remove: 0 };
  const defaultBureauDerogs = {
    collections: { ...defaultDerogItem },
    chargeoffs: { ...defaultDerogItem },
    late30: { ...defaultDerogItem },
    late60: { ...defaultDerogItem },
    late90plus: { ...defaultDerogItem },
    student_loans: { ...defaultDerogItem },
    auto_repo: { ...defaultDerogItem },
    auto_closure_balance: { ...defaultDerogItem },
    mortgage_foreclosure: { ...defaultDerogItem },
  };
  
  const resetForm = () => {
    setCurrentScores({ EQUIFAX: 580, EXPERIAN: 580, TRANSUNION: 580 });
    setDerogs({
      EQUIFAX: JSON.parse(JSON.stringify(defaultBureauDerogs)),
      EXPERIAN: JSON.parse(JSON.stringify(defaultBureauDerogs)),
      TRANSUNION: JSON.parse(JSON.stringify(defaultBureauDerogs)),
    });
    setCreditCards([]);
    setAuthorizedUsers([]);
    setFileData({ inquiries_0_12mo: 0, avg_age_years: null, oldest_age_years: null, revolving_accounts_count: 5 });
    setResults(null);
    setAgentAnalysis(null);
    setAgentInput('');
    setEnabledBureaus({ EQUIFAX: true, EXPERIAN: true, TRANSUNION: true });
    toast.success("Form reset");
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 md:p-6">
      {/* Mobile Navigation Menu */}
      <MobileNav />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Calculator className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Credit Score Simulator</h1>
              <p className="text-muted-foreground text-sm md:text-base">
                See how your score could improve with credit repair strategies
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/")} data-testid="back-to-dashboard-btn">
            <Home className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* MODE TOGGLE - Agent vs Manual */}
        <div className="mb-6">
          <div className="flex items-center justify-center gap-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-700/50">
            <Button
              variant={!agentMode ? "default" : "outline"}
              size="lg"
              onClick={() => setAgentMode(false)}
              className={`flex-1 max-w-xs ${!agentMode ? 'bg-gradient-to-r from-blue-600 to-purple-600' : ''}`}
              data-testid="manual-mode-btn"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Manual Input
            </Button>
            <Button
              variant={agentMode ? "default" : "outline"}
              size="lg"
              onClick={() => setAgentMode(true)}
              className={`flex-1 max-w-xs ${agentMode ? 'bg-gradient-to-r from-emerald-600 to-cyan-600' : ''}`}
              data-testid="agent-mode-btn"
            >
              <Bot className="w-5 h-5 mr-2" />
              AI Credit Agent
            </Button>
          </div>
        </div>
        
        {/* AI AGENT MODE UI */}
        {agentMode && (
          <Card className="border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-emerald-400">
                <Bot className="w-6 h-6" />
                AI Credit Repair Agent
              </CardTitle>
              <CardDescription>
                Paste or type your client's credit report summary below. The AI will analyze it and generate a detailed repair plan with score projections.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* CLIENT INFO - Name and Email for reports */}
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-emerald-500/30">
                <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Client Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Client Name *</Label>
                    <Input
                      placeholder="John Smith"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      className="bg-zinc-800 border-zinc-600"
                      data-testid="ai-client-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400">Client Email *</Label>
                    <Input
                      type="email"
                      placeholder="client@email.com"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      className="bg-zinc-800 border-zinc-600"
                      data-testid="ai-client-email"
                    />
                  </div>
                </div>
              </div>
              
              {/* Configuration Row - Confidence + Goal Mode + Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                {/* Confidence Level Selector */}
                <div className="space-y-2">
                  <Label className="text-sm text-zinc-400 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Data Confidence
                  </Label>
                  <Select value={confidenceLevel} onValueChange={setConfidenceLevel}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid="confidence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High (±25 pts)</SelectItem>
                      <SelectItem value="medium">Medium (±40 pts)</SelectItem>
                      <SelectItem value="low">Low (±60 pts)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    High = actual credit report in hand
                  </p>
                </div>
                
                {/* Goal Mode Presets */}
                <div className="space-y-2">
                  <Label className="text-sm text-zinc-400 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Goal Mode
                  </Label>
                  <Select value={goalMode || "none"} onValueChange={(v) => setGoalMode(v === "none" ? null : v)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid="goal-mode-select">
                      <SelectValue placeholder="Select goal..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific goal</SelectItem>
                      <SelectItem value="fastest_30_60">Fastest 30-60 day gains</SelectItem>
                      <SelectItem value="best_90_120">Best 90-120 day gains</SelectItem>
                      <SelectItem value="mortgage_ready">Mortgage ready</SelectItem>
                      <SelectItem value="auto_ready">Auto loan ready</SelectItem>
                      <SelectItem value="rental_ready">Rental approval focus</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500">
                    Prioritizes action ranking
                  </p>
                </div>
                
                {/* Fastest Win Toggle */}
                <div className="space-y-2">
                  <Label className="text-sm text-zinc-400 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Options
                  </Label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={prioritizeFastestWins}
                        onCheckedChange={setPrioritizeFastestWins}
                        data-testid="fastest-wins-toggle"
                      />
                      <span className="text-sm">Prioritize fastest wins</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={useStatementBalances}
                        onCheckedChange={setUseStatementBalances}
                        data-testid="statement-balances-toggle"
                      />
                      <span className="text-sm">Use statement balances</span>
                    </label>
                  </div>
                </div>
              </div>
              
              {/* ══════════════════════════════════════════════════════════════════════════ */}
              {/* TWO-STEP INPUT FLOW: 1. Format → 2. Edit → 3. Run Simulation */}
              {/* ══════════════════════════════════════════════════════════════════════════ */}
              
              {!showFormatted ? (
                /* STEP 1: Raw Input */
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-zinc-400 flex items-center gap-2">
                      <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">Step 1</span>
                      Describe the scenario (paste or type):
                    </Label>
                    <Textarea
                      placeholder="Example: Client has Equifax 510, TransUnion 513. They have 5 charged off credit cards and 5 charged off loans (10 total charge-offs), 4 collections from last year, 1 repo from 2 years ago. Also 3 credit cards - $700 limit/$500 balance, $1000 limit/$200 balance, $5000 limit/$3000 balance."
                      value={agentInput}
                      onChange={(e) => setAgentInput(e.target.value)}
                      className="min-h-[150px] bg-zinc-800 border-zinc-600"
                      data-testid="agent-input-textarea"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={formatInputText}
                      disabled={!agentInput.trim() || isFormatting}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      data-testid="format-input-btn"
                    >
                      {isFormatting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Formatting...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          Format & Organize
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-zinc-500">
                    Click "Format & Organize" to parse your text into structured sections you can review and edit before running the simulation.
                  </p>
                </div>
              ) : (
                /* STEP 2: Formatted View - Editable Sections */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-zinc-400 flex items-center gap-2">
                      <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">Step 2</span>
                      Review & Edit Formatted Data
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFormatted}
                      className="text-zinc-400 hover:text-white"
                    >
                      <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                      Back to Input
                    </Button>
                  </div>
                  
                  {formattedData && (
                    <div className="space-y-4 p-4 bg-zinc-800/50 rounded-lg border border-purple-500/30">
                      {/* BUREAUS SECTION */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-purple-400 flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          Credit Bureaus Detected
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {['EQUIFAX', 'EXPERIAN', 'TRANSUNION'].map(bureau => (
                            <label key={bureau} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formattedData.bureaus_mentioned?.includes(bureau) || false}
                                onChange={(e) => {
                                  const newBureaus = e.target.checked
                                    ? [...(formattedData.bureaus_mentioned || []), bureau]
                                    : (formattedData.bureaus_mentioned || []).filter(b => b !== bureau);
                                  updateFormattedField('bureaus_mentioned', newBureaus);
                                }}
                                className="rounded border-zinc-600"
                              />
                              <span className="text-sm">{bureau}</span>
                              {formattedData.scores?.[bureau] && (
                                <Input
                                  type="number"
                                  value={formattedData.scores[bureau] || ''}
                                  onChange={(e) => updateFormattedField(`scores.${bureau}`, parseInt(e.target.value) || 0)}
                                  className="w-20 h-7 text-sm bg-zinc-700 border-zinc-600"
                                  placeholder="Score"
                                />
                              )}
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-amber-400 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Only checked bureaus will be included in the simulation results
                        </p>
                      </div>
                      
                      {/* CHARGE-OFFS BREAKDOWN */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-red-400 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Charge-Offs Breakdown
                        </h4>
                        <div className="grid grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Credit Cards</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.charge_offs?.credit_cards || 0}
                              onChange={(e) => updateFormattedField('charge_offs.credit_cards', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Loans</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.charge_offs?.loans || 0}
                              onChange={(e) => updateFormattedField('charge_offs.loans', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Other</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.charge_offs?.other || 0}
                              onChange={(e) => updateFormattedField('charge_offs.other', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">TOTAL</Label>
                            <div className="h-10 flex items-center justify-center bg-red-500/20 border border-red-500/30 rounded text-lg font-bold text-red-400">
                              {formattedData.charge_offs?.total || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* COLLECTIONS */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-orange-400 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Collections
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Number of Collections</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.collections?.count || 0}
                              onChange={(e) => updateFormattedField('collections.count', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Total Balance ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.collections?.total_balance || 0}
                              onChange={(e) => updateFormattedField('collections.total_balance', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* LATE PAYMENTS */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Late Payments
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">30-Day Late</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.late_payments?.thirty_day || 0}
                              onChange={(e) => updateFormattedField('late_payments.thirty_day', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">60-Day Late</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.late_payments?.sixty_day || 0}
                              onChange={(e) => updateFormattedField('late_payments.sixty_day', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">90+ Day Late</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.late_payments?.ninety_plus || 0}
                              onChange={(e) => updateFormattedField('late_payments.ninety_plus', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* OTHER DEROGS */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-zinc-400">Other Derogatory Items</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Repossessions</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.repos || 0}
                              onChange={(e) => updateFormattedField('repos', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Foreclosures</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formattedData.foreclosures || 0}
                              onChange={(e) => updateFormattedField('foreclosures', parseInt(e.target.value) || 0)}
                              className="bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* TIMELINE EXPLANATION */}
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <h4 className="text-sm font-semibold text-blue-400 flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4" />
                          Expected Timeline: 72-120 Days
                        </h4>
                        <p className="text-xs text-blue-300 leading-relaxed">
                          <strong>What this means:</strong> Credit repair results typically take 72-120 days to fully reflect on your credit report. 
                          This includes 30-45 days for dispute processing by bureaus, plus additional time for creditors to update, 
                          and for new positive tradelines to report. Fastest wins (like paying down credit cards) can show within 30-45 days, 
                          while dispute removals typically take 45-90 days.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* ACTION BUTTONS */}
                  <div className="flex gap-3">
                    <Button
                      onClick={runSimulationFromFormatted}
                      disabled={agentLoading || !formattedData}
                      className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 flex-1"
                      data-testid="run-simulation-btn"
                    >
                      {agentLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running Simulation...
                        </>
                      ) : (
                        <>
                          <Calculator className="w-4 h-4 mr-2" />
                          Run AI Credit Simulation
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Reporting Timing Banner */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span className="text-xs text-blue-300">
                  Results assume balances are reporting at {useStatementBalances ? "statement closing dates" : "current real-time levels"}.
                  Credit bureaus update on different schedules (30-45 days typical).
                </span>
              </div>
              
              {/* Legacy direct analyze button (hidden when formatted view is shown) */}
              {!showFormatted && agentAnalysis && (
                <div className="flex gap-3 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAgentAnalysis(null);
                      setAgentInput('');
                      clearFormatted();
                    }}
                    data-testid="clear-analysis-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                </div>
              )}
              
              {/* AI Analysis Results - ENHANCED with Action Plan */}
              {agentAnalysis && (
                <div className="mt-6 space-y-4">
                  {/* CAP WARNINGS - Show first if any */}
                  {agentAnalysis.cap_warnings && agentAnalysis.cap_warnings.length > 0 && (
                    <div className="space-y-2">
                      {agentAnalysis.cap_warnings.map((warning, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg flex items-start gap-2 ${
                            warning.severity === 'critical' ? 'bg-red-500/20 border border-red-500/50' :
                            warning.severity === 'high' ? 'bg-orange-500/20 border border-orange-500/50' :
                            warning.severity === 'info' ? 'bg-blue-500/20 border border-blue-500/50' :
                            'bg-amber-500/20 border border-amber-500/50'
                          }`}
                        >
                          <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            warning.severity === 'critical' ? 'text-red-400' :
                            warning.severity === 'high' ? 'text-orange-400' :
                            warning.severity === 'info' ? 'text-blue-400' :
                            'text-amber-400'
                          }`} />
                          <span className={`text-sm ${
                            warning.severity === 'critical' ? 'text-red-300' :
                            warning.severity === 'high' ? 'text-orange-300' :
                            warning.severity === 'info' ? 'text-blue-300' :
                            'text-amber-300'
                          }`}>
                            {warning.message}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="border-t border-zinc-700 pt-4">
                    <h3 className="text-lg font-semibold text-emerald-400 flex items-center gap-2 mb-4">
                      <Sparkles className="w-5 h-5" />
                      Parsed Scenario
                      {agentAnalysis.parsing_method === 'ai' && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">AI Parsed</Badge>
                      )}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      {/* Scores */}
                      {agentAnalysis.scores && Object.keys(agentAnalysis.scores).length > 0 && (
                        <div className="bg-zinc-800/50 p-3 rounded-lg border border-zinc-700">
                          <h4 className="text-sm font-medium text-zinc-400 mb-2">Credit Scores</h4>
                          <div className="space-y-1">
                            {agentAnalysis.scores.equifax && (
                              <div className="flex justify-between">
                                <span className="text-zinc-500">Equifax:</span>
                                <span className={getScoreColor(agentAnalysis.scores.equifax)}>{agentAnalysis.scores.equifax}</span>
                              </div>
                            )}
                            {agentAnalysis.scores.experian && (
                              <div className="flex justify-between">
                                <span className="text-zinc-500">Experian:</span>
                                <span className={getScoreColor(agentAnalysis.scores.experian)}>{agentAnalysis.scores.experian}</span>
                              </div>
                            )}
                            {agentAnalysis.scores.transunion && (
                              <div className="flex justify-between">
                                <span className="text-zinc-500">TransUnion:</span>
                                <span className={getScoreColor(agentAnalysis.scores.transunion)}>{agentAnalysis.scores.transunion}</span>
                              </div>
                            )}
                          </div>
                          {/* Confidence Range */}
                          <div className="mt-2 pt-2 border-t border-zinc-700">
                            <p className="text-xs text-zinc-500">
                              Confidence: {confidenceLevel} (±{agentAnalysis.score_range || 40} pts)
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Derogs - Enhanced with age buckets */}
                      {agentAnalysis.derogs && Object.keys(agentAnalysis.derogs).length > 0 && (
                        <div className="bg-zinc-800/50 p-3 rounded-lg border border-zinc-700">
                          <h4 className="text-sm font-medium text-zinc-400 mb-2">Derogatory Items</h4>
                          <div className="space-y-1">
                            {(() => {
                              const d = agentAnalysis.derogs;
                              const items = [];
                              if (d.collections?.count > 0 || d.collections > 0) {
                                const count = d.collections?.count || d.collections;
                                const age = d.collections?.age_bucket || "";
                                items.push(
                                  <div key="coll" className="flex justify-between">
                                    <span className="text-zinc-500">Collections:</span>
                                    <span className="text-red-400">{count} {age && <span className="text-xs text-zinc-500">({age}mo)</span>}</span>
                                  </div>
                                );
                              }
                              if (d.chargeoffs?.count > 0 || d.chargeoffs > 0) {
                                const count = d.chargeoffs?.count || d.chargeoffs;
                                const age = d.chargeoffs?.age_bucket || "";
                                const hasBal = d.chargeoffs?.has_balance;
                                items.push(
                                  <div key="co" className="flex justify-between">
                                    <span className="text-zinc-500">Charge-Offs:</span>
                                    <span className="text-orange-400">{count} {age && <span className="text-xs text-zinc-500">({age}mo)</span>} {hasBal && <span className="text-xs text-red-400">w/bal</span>}</span>
                                  </div>
                                );
                              }
                              if (d.auto_repo?.count > 0 || d.auto_repo > 0) {
                                const count = d.auto_repo?.count || d.auto_repo;
                                items.push(
                                  <div key="repo" className="flex justify-between">
                                    <span className="text-zinc-500">Repossession:</span>
                                    <span className="text-red-400">{count}</span>
                                  </div>
                                );
                              }
                              const totalLates = (d.late30?.count || d.late30 || 0) + (d.late60?.count || d.late60 || 0) + (d.late90plus?.count || d.late90plus || 0);
                              if (totalLates > 0) {
                                items.push(
                                  <div key="lates" className="flex justify-between">
                                    <span className="text-zinc-500">Late Payments:</span>
                                    <span className="text-yellow-400">{totalLates}</span>
                                  </div>
                                );
                              }
                              return items;
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* Credit Cards */}
                      {agentAnalysis.credit_cards && agentAnalysis.credit_cards.length > 0 && (
                        <div className="bg-zinc-800/50 p-3 rounded-lg border border-zinc-700">
                          <h4 className="text-sm font-medium text-zinc-400 mb-2">Credit Cards</h4>
                          <div className="space-y-1">
                            {agentAnalysis.credit_cards.map((card, idx) => {
                              const util = card.limit > 0 ? Math.round((card.balance / card.limit) * 100) : 0;
                              return (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span className="text-zinc-500">Card {idx + 1}:</span>
                                  <span className={util > 50 ? 'text-red-400' : util > 30 ? 'text-yellow-400' : 'text-green-400'}>
                                    ${card.balance.toLocaleString()} / ${card.limit.toLocaleString()} ({util}%)
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* ACTION PLAN - COMPREHENSIVE BREAKDOWN */}
                    {agentAnalysis.action_plan && agentAnalysis.action_plan.length > 0 && (
                      <div className="space-y-4">
                        {/* SCORE JOURNEY - Before & After */}
                        <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 p-4 rounded-lg border border-purple-500/30">
                          <h4 className="text-lg font-medium text-purple-400 mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" />
                            Your Credit Score Journey
                          </h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Current Score */}
                            <div className="bg-zinc-800/70 p-4 rounded-lg border border-red-500/30 text-center">
                              <div className="text-xs text-zinc-500 mb-1">WHERE YOU ARE NOW</div>
                              <div className="text-3xl font-bold text-red-400">
                                {agentAnalysis.estimated_current_avg || 
                                  (agentAnalysis.scores ? Math.round(Object.values(agentAnalysis.scores).reduce((a,b) => a+b, 0) / Object.values(agentAnalysis.scores).length) : '---')}
                              </div>
                              <div className="text-xs text-zinc-500 mt-1">
                                {getTierName(agentAnalysis.estimated_current_avg || 500)} Credit
                              </div>
                            </div>
                            
                            {/* Arrow */}
                            <div className="hidden md:flex items-center justify-center">
                              <div className="flex flex-col items-center">
                                <ArrowRight className="w-10 h-10 text-emerald-400" />
                                <span className="text-xs text-emerald-400 font-bold mt-1">
                                  +{agentAnalysis.estimated_max_improvement || 0} pts
                                </span>
                              </div>
                            </div>
                            
                            {/* Potential Score */}
                            <div className="bg-zinc-800/70 p-4 rounded-lg border border-emerald-500/30 text-center">
                              <div className="text-xs text-zinc-500 mb-1">WHERE YOU COULD BE</div>
                              <div className="text-3xl font-bold text-emerald-400">
                                {agentAnalysis.estimated_potential_low || '---'}-{agentAnalysis.estimated_potential_high || '---'}
                              </div>
                              <div className="text-xs text-zinc-500 mt-1">
                                {getTierName(agentAnalysis.estimated_potential_high || 700)} Credit
                              </div>
                            </div>
                          </div>
                          
                          {/* Mobile Arrow */}
                          <div className="md:hidden flex justify-center my-3">
                            <div className="flex items-center gap-2 text-emerald-400">
                              <span className="text-sm font-bold">+{agentAnalysis.estimated_max_improvement || 0} points possible</span>
                              <TrendingUp className="w-5 h-5" />
                            </div>
                          </div>
                        </div>
                        
                        {/* DETAILED ACTION PLAN */}
                        <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 p-4 rounded-lg border border-emerald-500/30">
                          <h4 className="text-lg font-medium text-emerald-400 mb-4 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            Step-by-Step Action Plan
                          </h4>
                          
                          {/* Score Progression Bar */}
                          {(() => {
                            const currentAvg = agentAnalysis.estimated_current_avg || 
                              (agentAnalysis.scores ? Math.round(Object.values(agentAnalysis.scores).reduce((a,b) => a+b, 0) / Object.values(agentAnalysis.scores).length) : 500);
                            let runningScore = currentAvg;
                            
                            return (
                              <div className="mb-6 p-4 bg-zinc-800/50 rounded-lg">
                                <div className="text-xs text-zinc-500 mb-3 font-semibold">SCORE PROGRESSION AS EACH STEP COMPLETES:</div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="flex items-center gap-1 px-2 py-1 bg-red-500/20 rounded text-red-400 text-sm font-bold">
                                    Start: {currentAvg}
                                  </div>
                                  {agentAnalysis.action_plan.map((action, idx) => {
                                    // Extract min points from impact string (e.g., "+40-70 points" → 40)
                                    const impactMatch = action.estimated_impact?.match(/\+(\d+)/);
                                    const minPoints = impactMatch ? parseInt(impactMatch[1]) : 0;
                                    runningScore += minPoints;
                                    
                                    return (
                                      <div key={idx} className="flex items-center gap-1">
                                        <ArrowRight className="w-4 h-4 text-zinc-500" />
                                        <div className={`px-2 py-1 rounded text-sm font-bold ${
                                          idx === agentAnalysis.action_plan.length - 1 
                                            ? 'bg-emerald-500/20 text-emerald-400' 
                                            : 'bg-blue-500/20 text-blue-400'
                                        }`}>
                                          {Math.min(850, runningScore)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="text-xs text-zinc-500 mt-2">
                                  Each number shows your estimated score after completing that step
                                </div>
                              </div>
                            );
                          })()}
                          
                          {/* Step-by-step detailed breakdown */}
                          <div className="space-y-3">
                            {agentAnalysis.action_plan.map((action, idx) => (
                              <div 
                                key={idx} 
                                className={`p-4 rounded-lg border ${
                                  action.do_first ? 'bg-emerald-500/15 border-emerald-500/50' : 'bg-zinc-800/50 border-zinc-700'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  {/* Step Number */}
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0
                                    ${action.priority === 'critical' ? 'bg-red-500/30 text-red-400 border-2 border-red-500' : 
                                      action.priority === 'high' ? 'bg-orange-500/30 text-orange-400 border-2 border-orange-500' : 
                                      'bg-blue-500/30 text-blue-400 border-2 border-blue-500'}`}>
                                    {action.step}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    {/* Action Title & Badges */}
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                      <span className="font-semibold text-lg">{action.action}</span>
                                      {action.do_first && (
                                        <Badge className="bg-emerald-500/30 text-emerald-400 text-xs font-bold animate-pulse">
                                          🎯 DO THIS FIRST
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    {/* What This Does */}
                                    <div className="bg-zinc-700/30 p-3 rounded-lg mb-3">
                                      <div className="text-xs text-zinc-500 mb-1 font-semibold">WHAT THIS DOES:</div>
                                      <p className="text-sm text-zinc-300">{action.details}</p>
                                    </div>
                                    
                                    {/* Impact & Timeline */}
                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="bg-emerald-500/10 p-2 rounded-lg border border-emerald-500/30">
                                        <div className="text-xs text-emerald-500 font-semibold">SCORE IMPACT</div>
                                        <div className="text-lg font-bold text-emerald-400">{action.estimated_impact}</div>
                                      </div>
                                      <div className="bg-blue-500/10 p-2 rounded-lg border border-blue-500/30">
                                        <div className="text-xs text-blue-500 font-semibold">TIMELINE</div>
                                        <div className="text-sm font-bold text-blue-400 flex items-center gap-1">
                                          <Clock className="w-4 h-4" />
                                          {action.timeline}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* EXTRA SUGGESTIONS - Credit Building Strategies */}
                        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-4 rounded-lg border border-amber-500/30">
                          <h4 className="text-lg font-medium text-amber-400 mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Additional Credit Building Strategies
                          </h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Authorized User Strategy */}
                            <div className="bg-zinc-800/50 p-4 rounded-lg border border-purple-500/30">
                              <div className="flex items-center gap-2 mb-2">
                                <Users className="w-5 h-5 text-purple-400" />
                                <span className="font-semibold text-purple-400">Authorized User Tradeline</span>
                              </div>
                              <p className="text-sm text-zinc-400 mb-3">
                                Get added as an authorized user on someone's credit card with good history. 
                                This "borrows" their payment history and account age.
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Best tradeline age:</span>
                                  <span className="text-purple-400 font-bold">5-10+ years</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Ideal utilization:</span>
                                  <span className="text-purple-400 font-bold">&lt;10%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Estimated impact:</span>
                                  <span className="text-emerald-400 font-bold">+15-35 points</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Time to report:</span>
                                  <span className="text-blue-400">30-45 days</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Credit Card Paydown Strategy */}
                            {agentAnalysis.credit_cards && agentAnalysis.credit_cards.length > 0 && (() => {
                              const totalLimit = agentAnalysis.credit_cards.reduce((sum, c) => sum + (c.limit || 0), 0);
                              const totalBalance = agentAnalysis.credit_cards.reduce((sum, c) => sum + (c.balance || 0), 0);
                              const currentUtil = totalLimit > 0 ? Math.round((totalBalance / totalLimit) * 100) : 0;
                              const optimalBalance = Math.round(totalLimit * 0.10);
                              const paydownNeeded = Math.max(0, totalBalance - optimalBalance);
                              
                              return (
                                <div className="bg-zinc-800/50 p-4 rounded-lg border border-green-500/30">
                                  <div className="flex items-center gap-2 mb-2">
                                    <CreditCard className="w-5 h-5 text-green-400" />
                                    <span className="font-semibold text-green-400">Credit Card Optimization</span>
                                  </div>
                                  <p className="text-sm text-zinc-400 mb-3">
                                    Pay down your credit cards to 10% utilization or less for maximum score boost.
                                  </p>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Current utilization:</span>
                                      <span className={`font-bold ${currentUtil > 50 ? 'text-red-400' : currentUtil > 30 ? 'text-yellow-400' : 'text-green-400'}`}>
                                        {currentUtil}%
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Target utilization:</span>
                                      <span className="text-green-400 font-bold">10%</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Total to pay down:</span>
                                      <span className="text-amber-400 font-bold">${paydownNeeded.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-zinc-500">Estimated impact:</span>
                                      <span className="text-emerald-400 font-bold">
                                        +{currentUtil > 70 ? '50-80' : currentUtil > 50 ? '35-55' : currentUtil > 30 ? '20-35' : '10-20'} points
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            
                            {/* Secured Card Strategy */}
                            <div className="bg-zinc-800/50 p-4 rounded-lg border border-cyan-500/30">
                              <div className="flex items-center gap-2 mb-2">
                                <Wallet className="w-5 h-5 text-cyan-400" />
                                <span className="font-semibold text-cyan-400">Secured Credit Card</span>
                              </div>
                              <p className="text-sm text-zinc-400 mb-3">
                                Open a secured card with a $200-500 deposit. Use it for small purchases and pay in full monthly.
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Recommended deposit:</span>
                                  <span className="text-cyan-400 font-bold">$300-500</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Best usage:</span>
                                  <span className="text-cyan-400 font-bold">&lt;30% monthly</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Estimated impact:</span>
                                  <span className="text-emerald-400 font-bold">+10-25 points</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Time to see results:</span>
                                  <span className="text-blue-400">3-6 months</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Credit Builder Loan */}
                            <div className="bg-zinc-800/50 p-4 rounded-lg border border-pink-500/30">
                              <div className="flex items-center gap-2 mb-2">
                                <Building className="w-5 h-5 text-pink-400" />
                                <span className="font-semibold text-pink-400">Credit Builder Loan</span>
                              </div>
                              <p className="text-sm text-zinc-400 mb-3">
                                Get a credit builder loan from a credit union. Payments are reported monthly to build installment history.
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Typical amount:</span>
                                  <span className="text-pink-400 font-bold">$500-2,000</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Monthly payment:</span>
                                  <span className="text-pink-400 font-bold">$25-75</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Estimated impact:</span>
                                  <span className="text-emerald-400 font-bold">+15-30 points</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-zinc-500">Time to see results:</span>
                                  <span className="text-blue-400">2-4 months</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* SUMMARY & FINAL SCORE */}
                        <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 p-4 rounded-lg border border-zinc-600">
                          <h4 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                            <Target className="w-5 h-5 text-emerald-400" />
                            Summary: What Your Score Could Be
                          </h4>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-zinc-700">
                                  <th className="text-left py-2 text-zinc-400">After Completing</th>
                                  <th className="text-right py-2 text-zinc-400">Score Range</th>
                                  <th className="text-right py-2 text-zinc-400">Total Gain</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const currentAvg = agentAnalysis.estimated_current_avg || 
                                    (agentAnalysis.scores ? Math.round(Object.values(agentAnalysis.scores).reduce((a,b) => a+b, 0) / Object.values(agentAnalysis.scores).length) : 500);
                                  let runningScore = currentAvg;
                                  let totalGain = 0;
                                  
                                  return agentAnalysis.action_plan.map((action, idx) => {
                                    const impactMatch = action.estimated_impact?.match(/\+(\d+)-(\d+)/);
                                    const minPoints = impactMatch ? parseInt(impactMatch[1]) : 0;
                                    const maxPoints = impactMatch ? parseInt(impactMatch[2]) : minPoints;
                                    runningScore += minPoints;
                                    totalGain += minPoints;
                                    
                                    return (
                                      <tr key={idx} className="border-b border-zinc-800">
                                        <td className="py-2 text-zinc-300">
                                          <span className="text-zinc-500 mr-2">Step {action.step}:</span>
                                          {action.action}
                                        </td>
                                        <td className="py-2 text-right">
                                          <span className={idx === agentAnalysis.action_plan.length - 1 ? 'text-emerald-400 font-bold' : 'text-blue-400'}>
                                            {Math.min(850, runningScore)}-{Math.min(850, runningScore + (maxPoints - minPoints))}
                                          </span>
                                        </td>
                                        <td className="py-2 text-right text-emerald-400 font-bold">
                                          +{totalGain} pts
                                        </td>
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Final Numbers */}
                          <div className="mt-4 pt-4 border-t border-zinc-700 grid grid-cols-2 gap-4">
                            <div className="text-center">
                              <div className="text-xs text-zinc-500">STARTING SCORE</div>
                              <div className="text-2xl font-bold text-red-400">
                                {agentAnalysis.estimated_current_avg || '---'}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-zinc-500">FINAL PROJECTED SCORE</div>
                              <div className="text-2xl font-bold text-emerald-400">
                                {agentAnalysis.estimated_potential_low}-{agentAnalysis.estimated_potential_high}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* This Week's Focus */}
                        {agentAnalysis.email_summary && (
                          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <h5 className="text-sm font-bold text-blue-400 mb-2 flex items-center gap-2">
                              <Target className="w-4 h-4" />
                              THIS WEEK'S PRIORITY:
                            </h5>
                            <p className="text-lg text-white">{agentAnalysis.email_summary.this_week}</p>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                          <Button
                            onClick={() => {
                              setAgentMode(false);
                              runSimulation();
                            }}
                            className="bg-gradient-to-r from-blue-600 to-purple-600"
                            data-testid="run-full-simulation-btn"
                          >
                            <Calculator className="w-4 h-4 mr-2" />
                            Run Full Simulation
                          </Button>
                          
                          <Button
                            onClick={() => handleEmail()}
                            disabled={!clientEmail || sendingEmail}
                            className={`bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 ${sendingEmail ? "animate-pulse" : ""}`}
                            data-testid="email-analysis-btn"
                          >
                            {sendingEmail ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Mail className="w-4 h-4 mr-2" />
                                Email Full Report to Client
                              </>
                            )}
                          </Button>
                        </div>
                        
                        {!clientEmail && (
                          <p className="text-sm text-amber-400 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Enter client email above to enable email sending
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Fallback: Old-style Recommendations if no action_plan */}
                    {!agentAnalysis.action_plan && agentAnalysis.recommendations && agentAnalysis.recommendations.length > 0 && (
                      <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 p-4 rounded-lg border border-emerald-500/30">
                        <h4 className="text-lg font-medium text-emerald-400 mb-3 flex items-center gap-2">
                          <Target className="w-5 h-5" />
                          Recommended Actions (by Priority)
                        </h4>
                        <div className="space-y-3">
                          {agentAnalysis.recommendations.map((rec, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                                ${rec.priority === 1 ? 'bg-red-500/20 text-red-400' : 
                                  rec.priority === 2 ? 'bg-orange-500/20 text-orange-400' : 
                                  'bg-yellow-500/20 text-yellow-400'}`}>
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{rec.action}</span>
                                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                                    {rec.impact}
                                  </Badge>
                                </div>
                                <p className="text-sm text-zinc-400 mt-1">{rec.details}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 flex gap-3">
                          <Button
                            onClick={() => {
                              setAgentMode(false);
                              runSimulation();
                            }}
                            className="bg-gradient-to-r from-blue-600 to-purple-600"
                            data-testid="run-full-simulation-btn-fallback"
                          >
                            <Calculator className="w-4 h-4 mr-2" />
                            Run Full Simulation
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* MANUAL INPUT MODE */}
        {!agentMode && (
          <>
        {/* Client Info + Model Selection */}
        <Card className="border-purple-500/30 bg-gradient-to-r from-purple-500/10 to-blue-500/10 mb-6">
          <CardContent className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400">Client Name</Label>
                <Input
                  placeholder="Enter client name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="bg-zinc-800 border-zinc-600"
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400">Client Email</Label>
                <Input
                  type="email"
                  placeholder="client@email.com"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="bg-zinc-800 border-zinc-600"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400">Scoring Model</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid="model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FICO8">FICO 8</SelectItem>
                    <SelectItem value="VANTAGE3">VantageScore 3</SelectItem>
                    <SelectItem value="BLEND">Blend (Average)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400">Recent Inquiries</Label>
                <Input
                  type="number"
                  min={0}
                  max={20}
                  placeholder="0"
                  value={fileData.inquiries_0_12mo || ""}
                  onChange={(e) => setFileData({ ...fileData, inquiries_0_12mo: parseInt(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-600"
                  data-testid="inquiries-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* NEW: Simulation Configuration Card */}
        <Card className="border-cyan-500/30 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Confidence Level */}
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Data Confidence
                </Label>
                <Select value={confidenceLevel} onValueChange={setConfidenceLevel}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid="manual-confidence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High (±25 pts)</SelectItem>
                    <SelectItem value="medium">Medium (±40 pts)</SelectItem>
                    <SelectItem value="low">Low (±60 pts)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Goal Mode */}
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Goal Mode
                </Label>
                <Select value={goalMode || "none"} onValueChange={(v) => setGoalMode(v === "none" ? null : v)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid="manual-goal-select">
                    <SelectValue placeholder="Select goal..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific goal</SelectItem>
                    <SelectItem value="fastest_30_60">Fastest 30-60 day gains</SelectItem>
                    <SelectItem value="best_90_120">Best 90-120 day gains</SelectItem>
                    <SelectItem value="mortgage_ready">Mortgage ready</SelectItem>
                    <SelectItem value="auto_ready">Auto loan ready</SelectItem>
                    <SelectItem value="rental_ready">Rental approval focus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Reporting Toggle */}
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Balance Reporting
                </Label>
                <div className="flex items-center gap-3 h-9 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={useStatementBalances}
                      onCheckedChange={setUseStatementBalances}
                      data-testid="manual-statement-toggle"
                    />
                    <span className="text-xs text-zinc-400">
                      {useStatementBalances ? "Statement balances" : "Current balances"}
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Fastest Win Toggle */}
              <div className="space-y-2">
                <Label className="text-sm text-zinc-400 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Options
                </Label>
                <div className="flex items-center gap-3 h-9 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={prioritizeFastestWins}
                      onCheckedChange={setPrioritizeFastestWins}
                      data-testid="manual-fastest-toggle"
                    />
                    <span className="text-xs text-zinc-400">Prioritize fastest wins</span>
                  </label>
                </div>
              </div>
            </div>
            
            {/* Goal Mode Info Banner */}
            {goalMode && (
              <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-300 flex items-center gap-2">
                <Target className="w-4 h-4 flex-shrink-0" />
                {goalMode === "mortgage_ready" && "Mortgage lenders prefer: <30% utilization, no recent lates (12mo), score 620+ (FHA) or 740+ (best rates)."}
                {goalMode === "auto_ready" && "Auto lenders focus on: recent payment history, existing auto tradeline performance, score 600+ (subprime) or 700+ (prime)."}
                {goalMode === "rental_ready" && "Rental focus: remove collections, show stable payment history, score 600+ minimum for most landlords."}
                {goalMode === "fastest_30_60" && "Fastest wins: Focus on utilization paydown and goodwill letters first - these can show results in 30-45 days."}
                {goalMode === "best_90_120" && "90-120 day plan: Includes dispute cycles, allowing time for deletions and updates to reflect."}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Inputs */}
          <div className="space-y-4">
            {/* NEW: Current Scores Section with Bureau Toggles */}
            <Card className="border-purple-500/30 bg-purple-500/5">
              <CardHeader className="pb-2">
                <CardTitle 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedSections(prev => ({ ...prev, scores: !prev.scores }))}
                >
                  <div className="flex items-center gap-2 text-purple-400">
                    <Target className="w-5 h-5" />
                    Current Credit Scores
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {getEnabledBureaus().length}/3 Bureaus
                    </Badge>
                    {expandedSections.scores ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </CardTitle>
                <p className="text-xs text-zinc-500 mt-1">
                  Enter current scores for each bureau. Toggle OFF bureaus you don't have data for (e.g., Credit Karma only shows Equifax & TransUnion)
                </p>
              </CardHeader>
              {expandedSections.scores && (
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {BUREAUS.map((bureau) => {
                      const isEnabled = enabledBureaus[bureau];
                      const bureauColors = {
                        EQUIFAX: { border: 'border-red-500/30', bg: 'bg-red-500/10', text: 'text-red-400' },
                        EXPERIAN: { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400' },
                        TRANSUNION: { border: 'border-green-500/30', bg: 'bg-green-500/10', text: 'text-green-400' },
                      };
                      const colors = bureauColors[bureau];
                      
                      return (
                        <div 
                          key={bureau}
                          className={`p-4 rounded-lg border transition-all ${isEnabled ? `${colors.border} ${colors.bg}` : 'border-zinc-700/50 bg-zinc-800/30 opacity-50'}`}
                        >
                          {/* Bureau Header with Toggle */}
                          <div className="flex items-center justify-between mb-3 gap-4">
                            <span className={`font-semibold text-sm ${isEnabled ? colors.text : 'text-zinc-500'}`}>
                              {bureau}
                            </span>
                            <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                              <span className="text-xs text-zinc-500">{isEnabled ? 'ON' : 'OFF'}</span>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={isEnabled}
                                onClick={() => toggleBureau(bureau)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isEnabled ? 'bg-green-500' : 'bg-zinc-600'}`}
                                data-testid={`bureau-toggle-${bureau.toLowerCase()}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </label>
                          </div>
                          
                          {/* Score Input */}
                          <div className="space-y-2">
                            <Input
                              type="number"
                              min={300}
                              max={850}
                              step={1}
                              disabled={!isEnabled}
                              value={currentScores[bureau]}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') {
                                  setCurrentScores({ ...currentScores, [bureau]: '' });
                                } else {
                                  const num = parseInt(val);
                                  if (!isNaN(num)) {
                                    setCurrentScores({ ...currentScores, [bureau]: num });
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                const val = parseInt(e.target.value);
                                if (isNaN(val) || val < 300) {
                                  setCurrentScores({ ...currentScores, [bureau]: 300 });
                                } else if (val > 850) {
                                  setCurrentScores({ ...currentScores, [bureau]: 850 });
                                }
                              }}
                              className={`text-center text-xl font-bold h-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${isEnabled ? 'bg-zinc-800 border-zinc-600' : 'bg-zinc-900 border-zinc-700'}`}
                              data-testid={`current-score-${bureau.toLowerCase()}`}
                            />
                            {isEnabled && (
                              <Badge className={`w-full justify-center ${getScoreBgColor(currentScores[bureau] || 300)} py-1`}>
                                {getTierName(currentScores[bureau] || 300)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Credit Karma Helper */}
                  <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span className="text-xs text-amber-300">
                      <strong>Credit Karma users:</strong> Toggle OFF Experian - Credit Karma only provides Equifax & TransUnion scores
                    </span>
                  </div>
                </CardContent>
              )}
            </Card>
            
            {/* Derogatory Items - Side by Side for All Enabled Bureaus */}
            <Card className="border-red-500/30 bg-red-500/5">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    Derogatory Items by Bureau
                    <Badge variant="outline" className="text-xs ml-2">
                      {getEnabledBureaus().length} Bureau{getEnabledBureaus().length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyDerogsToAllBureaus}
                    className="text-xs"
                    data-testid="copy-to-all-bureaus-btn"
                  >
                    Copy to All Bureaus
                  </Button>
                </CardTitle>
                <p className="text-xs text-zinc-500">
                  Configure derogatory items for each bureau side-by-side
                </p>
              </CardHeader>
              <CardContent>
                <Collapsible
                  open={expandedSections.derogs}
                  onOpenChange={(v) => setExpandedSections({ ...expandedSections, derogs: v })}
                >
                  <CollapsibleTrigger className="w-full mb-4">
                    <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg cursor-pointer hover:bg-red-500/20">
                      <div className="flex items-center gap-2 text-red-400">
                        <span className="font-medium text-sm">Derogatory Items (Have → Remove)</span>
                        {(() => {
                          const totalHave = Object.entries(derogs).reduce((total, [bureau, items]) => 
                            enabledBureaus[bureau] ? total + Object.values(items).reduce((a, item) => a + (item.total || 0), 0) : total, 0
                          );
                          const totalRemove = Object.entries(derogs).reduce((total, [bureau, items]) => 
                            enabledBureaus[bureau] ? total + Object.values(items).reduce((a, item) => a + (item.to_remove || 0), 0) : total, 0
                          );
                          if (totalHave > 0) {
                            return (
                              <Badge className="bg-red-500/30">
                                {totalRemove}/{totalHave} removing
                              </Badge>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {expandedSections.derogs ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4">
                    {/* Explanation */}
                    <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-zinc-400">
                          <p className="font-semibold text-blue-400 mb-1">How to use:</p>
                          <p><strong>"Have"</strong> = Total items currently on report</p>
                          <p><strong>"Remove"</strong> = Items you're disputing</p>
                          <p className="text-amber-400 mt-1">Remaining items still affect the projected score!</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Side-by-Side Bureau Grid */}
                    <div className={`grid gap-4 ${getEnabledBureaus().length === 1 ? 'grid-cols-1' : getEnabledBureaus().length === 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-3'}`}>
                      {BUREAUS.map((bureau) => {
                        if (!enabledBureaus[bureau]) return null;
                        
                        const bureauColors = {
                          EQUIFAX: { border: 'border-red-500/50', bg: 'bg-red-500/5', text: 'text-red-400', header: 'bg-red-500/20' },
                          EXPERIAN: { border: 'border-blue-500/50', bg: 'bg-blue-500/5', text: 'text-blue-400', header: 'bg-blue-500/20' },
                          TRANSUNION: { border: 'border-green-500/50', bg: 'bg-green-500/5', text: 'text-green-400', header: 'bg-green-500/20' },
                        };
                        const colors = bureauColors[bureau];
                        const bureauTotal = Object.values(derogs[bureau]).reduce((a, item) => a + (item.total || 0), 0);
                        const bureauToRemove = Object.values(derogs[bureau]).reduce((a, item) => a + (item.to_remove || 0), 0);
                        
                        return (
                          <div key={bureau} className={`rounded-lg border ${colors.border} ${colors.bg} overflow-hidden`}>
                            {/* Bureau Header */}
                            <div className={`${colors.header} p-3 flex items-center justify-between`}>
                              <span className={`font-bold ${colors.text}`}>{bureau}</span>
                              {bureauTotal > 0 && (
                                <Badge className={`${colors.bg} ${colors.text} border ${colors.border}`}>
                                  {bureauToRemove}/{bureauTotal} removing
                                </Badge>
                              )}
                            </div>
                            
                            {/* Bureau Derogs - ENHANCED with age buckets and balance tracking */}
                            <div className="p-3 space-y-2">
                              {/* Collections - with age bucket and balance */}
                              <DerogInputPair bureau={bureau} field="collections" label="Collections" color="red" showAge={true} showBalance={true} />
                              
                              {/* Charge-offs - with age bucket and balance reporting toggle */}
                              <DerogInputPair bureau={bureau} field="chargeoffs" label="Charge-Offs" color="orange" showAge={true} showBalance={true} showHasBalance={true} />
                              
                              {/* Late Payments - with age buckets */}
                              <DerogInputPair bureau={bureau} field="late30" label="30-Day Late" color="yellow" showAge={true} />
                              <DerogInputPair bureau={bureau} field="late60" label="60-Day Late" color="yellow" showAge={true} />
                              <DerogInputPair bureau={bureau} field="late90plus" label="90+ Day Late" color="yellow" showAge={true} />
                              
                              {/* Student Loans, Auto, Mortgage - with special flags */}
                              <div className="border-t border-zinc-700 my-2 pt-2">
                                <DerogInputPair bureau={bureau} field="student_loans" label="Student Loans" color="purple" showDelinquent={true} />
                                <DerogInputPair bureau={bureau} field="auto_repo" label="Auto Repossession" color="red" showAge={true} showDeficiency={true} />
                                <DerogInputPair bureau={bureau} field="auto_closure_balance" label="Auto Closure w/ Balance" color="orange" />
                                <DerogInputPair bureau={bureau} field="mortgage_foreclosure" label="Mortgage Foreclosure" color="red" showAge={true} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Disabled bureaus notice */}
                    {BUREAUS.some(b => !enabledBureaus[b]) && (
                      <div className="text-xs text-zinc-500 flex items-center gap-2 p-2 bg-zinc-800/30 rounded">
                        <AlertCircle className="w-3 h-3" />
                        {BUREAUS.filter(b => !enabledBureaus[b]).join(", ")} disabled - enable in "Current Credit Scores" to configure
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>

            {/* Credit Card Paydown Simulator */}
            <Card className="border-green-500/30 bg-green-500/5">
              <Collapsible
                open={expandedSections.cards}
                onOpenChange={(v) => setExpandedSections({ ...expandedSections, cards: v })}
              >
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-green-500/10">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2 text-green-400">
                        <Wallet className="w-5 h-5" />
                        Credit Card Paydown Simulator
                        {creditCards.length > 0 && (
                          <Badge className="bg-green-500/30 ml-2">{creditCards.length} cards</Badge>
                        )}
                      </CardTitle>
                      {expandedSections.cards ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <p className="text-xs text-zinc-400">
                      Add your credit cards to see how paying down balances affects your score.
                    </p>

                    {/* Card List */}
                    {creditCards.map((card, idx) => {
                      const currentUtil = calculateUtilization(card.current_balance, card.credit_limit);
                      const targetUtil = calculateUtilization(card.target_balance, card.credit_limit);
                      const paydown = card.current_balance - card.target_balance;
                      
                      return (
                        <div key={card.id} className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
                          <div className="flex justify-between items-center">
                            <Input
                              placeholder="Card name (e.g., Chase Freedom)"
                              value={card.name}
                              onChange={(e) => updateCreditCard(idx, "name", e.target.value)}
                              className="bg-zinc-700 border-zinc-600 w-48"
                              data-testid={`card-name-${idx}`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:bg-red-500/20"
                              onClick={() => removeCreditCard(idx)}
                              data-testid={`remove-card-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-400">Credit Limit</Label>
                              <Input
                                type="number"
                                min={0}
                                value={card.credit_limit}
                                onChange={(e) => updateCreditCard(idx, "credit_limit", parseInt(e.target.value) || 0)}
                                className="h-9 bg-zinc-700 border-zinc-600"
                                data-testid={`card-limit-${idx}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-400">Current Balance</Label>
                              <Input
                                type="number"
                                min={0}
                                value={card.current_balance}
                                onChange={(e) => updateCreditCard(idx, "current_balance", parseInt(e.target.value) || 0)}
                                className="h-9 bg-zinc-700 border-zinc-600"
                                data-testid={`card-current-${idx}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-400">Target Balance</Label>
                              <Input
                                type="number"
                                min={0}
                                value={card.target_balance}
                                onChange={(e) => updateCreditCard(idx, "target_balance", parseInt(e.target.value) || 0)}
                                className="h-9 bg-zinc-700 border-zinc-600"
                                data-testid={`card-target-${idx}`}
                              />
                            </div>
                          </div>
                          
                          {/* Utilization Preview */}
                          <div className="flex items-center justify-between text-xs p-2 bg-zinc-900/50 rounded">
                            <div className="flex items-center gap-2">
                              <span className={`font-bold ${currentUtil > 30 ? 'text-red-400' : 'text-green-400'}`}>
                                {currentUtil}%
                              </span>
                              <ArrowRight className="w-3 h-3 text-zinc-500" />
                              <span className={`font-bold ${targetUtil > 30 ? 'text-amber-400' : 'text-green-400'}`}>
                                {targetUtil}%
                              </span>
                              <span className="text-zinc-500">utilization</span>
                            </div>
                            {paydown > 0 && (
                              <span className="text-green-400">
                                Pay down ${paydown.toLocaleString()}
                              </span>
                            )}
                          </div>
                          
                          {/* Optimal 10% Utilization Quick Actions */}
                          <div className="flex items-center justify-between text-xs p-2 bg-blue-500/10 border border-blue-500/30 rounded">
                            <div className="flex items-center gap-2">
                              <Target className="w-3 h-3 text-blue-400" />
                              <span className="text-blue-400">
                                Optimal (10%): ${Math.round(card.credit_limit * 0.1).toLocaleString()} balance
                              </span>
                            </div>
                            <button
                              onClick={() => updateCreditCard(idx, "target_balance", Math.round(card.credit_limit * 0.1))}
                              className="text-blue-400 hover:text-blue-300 underline font-medium"
                            >
                              Set to 10%
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add Card Button */}
                    <Button
                      variant="outline"
                      onClick={addCreditCard}
                      className="w-full border-dashed border-green-500/50 text-green-400 hover:bg-green-500/10"
                      data-testid="add-credit-card-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Credit Card
                    </Button>
                    
                    {/* Set All to 10% Button */}
                    {creditCards.length > 1 && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          creditCards.forEach((card, idx) => {
                            updateCreditCard(idx, "target_balance", Math.round(card.credit_limit * 0.1));
                          });
                          toast.success("All cards set to 10% target utilization");
                        }}
                        className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Target className="w-4 h-4 mr-2" />
                        Set All Cards to 10% Utilization
                      </Button>
                    )}

                    {/* Total Utilization Summary */}
                    {creditCards.length > 0 && (
                      <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-green-400 font-medium">Total Utilization</span>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${getTotalUtilization(false) > 30 ? 'text-red-400' : 'text-green-400'}`}>
                              {getTotalUtilization(false)}%
                            </span>
                            <ArrowRight className="w-4 h-4 text-zinc-500" />
                            <span className={`font-bold ${getTotalUtilization(true) > 30 ? 'text-amber-400' : 'text-green-400'}`}>
                              {getTotalUtilization(true)}%
                            </span>
                          </div>
                        </div>
                        
                        {/* 10% Utilization Projection */}
                        {(() => {
                          const totalLimit = creditCards.reduce((sum, c) => sum + c.credit_limit, 0);
                          const optimal10Balance = Math.round(totalLimit * 0.1);
                          const currentTotal = creditCards.reduce((sum, c) => sum + c.current_balance, 0);
                          const paydownTo10 = Math.max(0, currentTotal - optimal10Balance);
                          
                          if (getTotalUtilization(false) > 10 && paydownTo10 > 0) {
                            return (
                              <div className="text-xs p-2 bg-blue-500/10 border border-blue-500/30 rounded">
                                <div className="flex items-center gap-2 text-blue-400">
                                  <Target className="w-3 h-3" />
                                  <span className="font-semibold">10% Utilization Projection:</span>
                                </div>
                                <p className="text-zinc-400 mt-1">
                                  Pay down <span className="text-blue-400 font-bold">${paydownTo10.toLocaleString()}</span> total 
                                  to reach 10% utilization (${optimal10Balance.toLocaleString()} total balance)
                                </p>
                                <p className="text-green-400 mt-1 font-medium">
                                  Estimated score boost: +20 to +40 points
                                </p>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* POSITIVE TRADELINES - Auto Loans, Personal Loans, Zero Balance Cards */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            
            {/* Auto Loans */}
            <Card className="border-green-500/30 bg-green-500/5">
              <Collapsible
                open={expandedSections.autoLoans}
                onOpenChange={(v) => setExpandedSections({ ...expandedSections, autoLoans: v })}
              >
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-green-500/10">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2 text-green-400">
                        <Car className="w-5 h-5" />
                        Auto Loans (Positive Tradelines)
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {autoLoans.length > 0 && (
                          <Badge className="bg-green-500/20 text-green-400">
                            {autoLoans.length} loan{autoLoans.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.autoLoans ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-zinc-500 mb-3">
                      Add current auto loans being paid on time. These boost your score through payment history and account mix.
                    </p>
                    
                    {autoLoans.map((loan, idx) => (
                      <div key={loan.id} className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
                        <div className="flex justify-between items-center">
                          <Input
                            placeholder="Lender name (e.g., Toyota Financial)"
                            value={loan.lender_name}
                            onChange={(e) => updateAutoLoan(idx, "lender_name", e.target.value)}
                            className="bg-zinc-700 border-zinc-600 w-48"
                          />
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-500/20 text-green-400">
                              +{Math.min(25, 5 + (loan.months_paid || 0) * 0.5)} pts est.
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:bg-red-500/20"
                              onClick={() => removeAutoLoan(idx)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Original Amount ($)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.original_amount}
                              onChange={(e) => updateAutoLoan(idx, "original_amount", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Current Balance ($)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.current_balance}
                              onChange={(e) => updateAutoLoan(idx, "current_balance", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Monthly Payment ($)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.monthly_payment}
                              onChange={(e) => updateAutoLoan(idx, "monthly_payment", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Months Paid On Time</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.months_paid}
                              onChange={(e) => updateAutoLoan(idx, "months_paid", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="space-y-1 flex-1">
                            <Label className="text-xs text-zinc-400">Date Opened</Label>
                            <Input
                              type="date"
                              value={loan.date_opened}
                              onChange={(e) => updateAutoLoan(idx, "date_opened", e.target.value)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-5">
                            <Switch
                              checked={loan.on_time_payments}
                              onCheckedChange={(v) => updateAutoLoan(idx, "on_time_payments", v)}
                            />
                            <Label className="text-xs">All Payments On Time</Label>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      className="w-full border-green-500/50 text-green-400 hover:bg-green-500/10"
                      onClick={addAutoLoan}
                      data-testid="add-auto-loan-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Auto Loan
                    </Button>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
            
            {/* Personal Loans */}
            <Card className="border-cyan-500/30 bg-cyan-500/5">
              <Collapsible
                open={expandedSections.personalLoans}
                onOpenChange={(v) => setExpandedSections({ ...expandedSections, personalLoans: v })}
              >
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-cyan-500/10">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2 text-cyan-400">
                        <DollarSign className="w-5 h-5" />
                        Personal Loans (Positive Tradelines)
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {personalLoans.length > 0 && (
                          <Badge className="bg-cyan-500/20 text-cyan-400">
                            {personalLoans.length} loan{personalLoans.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.personalLoans ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-zinc-500 mb-3">
                      Add personal/signature loans being paid on time. Installment loans diversify your credit mix.
                    </p>
                    
                    {personalLoans.map((loan, idx) => (
                      <div key={loan.id} className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
                        <div className="flex justify-between items-center">
                          <Input
                            placeholder="Lender name (e.g., Discover)"
                            value={loan.lender_name}
                            onChange={(e) => updatePersonalLoan(idx, "lender_name", e.target.value)}
                            className="bg-zinc-700 border-zinc-600 w-48"
                          />
                          <div className="flex items-center gap-2">
                            <Badge className="bg-cyan-500/20 text-cyan-400">
                              +{Math.min(20, 5 + (loan.months_paid || 0) * 0.4)} pts est.
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:bg-red-500/20"
                              onClick={() => removePersonalLoan(idx)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Original Amount ($)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.original_amount}
                              onChange={(e) => updatePersonalLoan(idx, "original_amount", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Current Balance ($)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.current_balance}
                              onChange={(e) => updatePersonalLoan(idx, "current_balance", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Monthly Payment ($)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.monthly_payment}
                              onChange={(e) => updatePersonalLoan(idx, "monthly_payment", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Months Paid On Time</Label>
                            <Input
                              type="number"
                              min={0}
                              value={loan.months_paid}
                              onChange={(e) => updatePersonalLoan(idx, "months_paid", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="space-y-1 flex-1">
                            <Label className="text-xs text-zinc-400">Date Opened</Label>
                            <Input
                              type="date"
                              value={loan.date_opened}
                              onChange={(e) => updatePersonalLoan(idx, "date_opened", e.target.value)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-5">
                            <Switch
                              checked={loan.on_time_payments}
                              onCheckedChange={(v) => updatePersonalLoan(idx, "on_time_payments", v)}
                            />
                            <Label className="text-xs">All Payments On Time</Label>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      className="w-full border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                      onClick={addPersonalLoan}
                      data-testid="add-personal-loan-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Personal Loan
                    </Button>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
            
            {/* Zero Balance Credit Cards */}
            <Card className="border-emerald-500/30 bg-emerald-500/5">
              <Collapsible
                open={expandedSections.zeroBalanceCards}
                onOpenChange={(v) => setExpandedSections({ ...expandedSections, zeroBalanceCards: v })}
              >
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-emerald-500/10">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2 text-emerald-400">
                        <CreditCard className="w-5 h-5" />
                        Zero Balance Cards (Available Credit)
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        {zeroBalanceCards.length > 0 && (
                          <Badge className="bg-emerald-500/20 text-emerald-400">
                            {zeroBalanceCards.length} card{zeroBalanceCards.length > 1 ? 's' : ''} • ${zeroBalanceCards.reduce((sum, c) => sum + (c.credit_limit || 0), 0).toLocaleString()} available
                          </Badge>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${expandedSections.zeroBalanceCards ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-zinc-500 mb-3">
                      Add credit cards with $0 balance. These help by lowering overall utilization and adding available credit.
                    </p>
                    
                    {zeroBalanceCards.map((card, idx) => (
                      <div key={card.id} className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
                        <div className="flex justify-between items-center">
                          <Input
                            placeholder="Card name (e.g., Chase Freedom)"
                            value={card.card_name}
                            onChange={(e) => updateZeroBalanceCard(idx, "card_name", e.target.value)}
                            className="bg-zinc-700 border-zinc-600 w-48"
                          />
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              +{Math.min(15, 3 + (card.age_years || 0) * 2)} pts est.
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:bg-red-500/20"
                              onClick={() => removeZeroBalanceCard(idx)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Credit Limit ($)</Label>
                            <Input
                              type="number"
                              min={0}
                              value={card.credit_limit}
                              onChange={(e) => updateZeroBalanceCard(idx, "credit_limit", parseInt(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Account Age (Years)</Label>
                            <Input
                              type="number"
                              min={0}
                              step={0.5}
                              value={card.age_years}
                              onChange={(e) => updateZeroBalanceCard(idx, "age_years", parseFloat(e.target.value) || 0)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-400">Date Opened</Label>
                            <Input
                              type="date"
                              value={card.date_opened}
                              onChange={(e) => updateZeroBalanceCard(idx, "date_opened", e.target.value)}
                              className="h-9 bg-zinc-700 border-zinc-600"
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={card.on_time_payments}
                            onCheckedChange={(v) => updateZeroBalanceCard(idx, "on_time_payments", v)}
                          />
                          <Label className="text-xs">Perfect Payment History</Label>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      className="w-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={addZeroBalanceCard}
                      data-testid="add-zero-balance-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Zero Balance Card
                    </Button>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

            {/* Authorized User Tradelines */}
            <Card className="border-purple-500/30 bg-purple-500/5">
              <Collapsible
                open={expandedSections.au}
                onOpenChange={(v) => setExpandedSections({ ...expandedSections, au: v })}
              >
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-purple-500/10">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2 text-purple-400">
                        <Users className="w-5 h-5" />
                        Authorized User Tradelines
                        {authorizedUsers.length > 0 && (
                          <Badge className="bg-purple-500/30 ml-2">{authorizedUsers.length} AU</Badge>
                        )}
                      </CardTitle>
                      {expandedSections.au ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <p className="text-xs text-zinc-400">
                      Add AU tradelines to boost credit history and available credit.
                    </p>

                    {/* AU List */}
                    {authorizedUsers.map((au, idx) => {
                      const impact = calculateAUImpact(au, idx);
                      
                      return (
                        <div key={au.id} className="p-4 bg-zinc-800/50 rounded-lg space-y-3">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <Input
                                placeholder="Card name (optional)"
                                value={au.card_name}
                                onChange={(e) => updateAU(idx, "card_name", e.target.value)}
                                className="bg-zinc-700 border-zinc-600 w-40"
                                data-testid={`au-name-${idx}`}
                              />
                              <Badge className="bg-purple-500/20 text-purple-400">
                                +{impact} pts est.
                              </Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:bg-red-500/20"
                              onClick={() => removeAU(idx)}
                              data-testid={`remove-au-${idx}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {/* AU Age Bucket */}
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-400">AU Age</Label>
                              <Select
                                value={au.au_age_bucket || "5-10"}
                                onValueChange={(v) => {
                                  updateAU(idx, "au_age_bucket", v);
                                  // Also update legacy years field
                                  const yearsMap = { "<2": 1, "2-5": 3.5, "5-10": 7.5, "10+": 12 };
                                  updateAU(idx, "au_history_years", yearsMap[v] || 7);
                                }}
                              >
                                <SelectTrigger className="h-9 bg-zinc-700 border-zinc-600" data-testid={`au-age-bucket-${idx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="<2">&lt;2 years</SelectItem>
                                  <SelectItem value="2-5">2-5 years</SelectItem>
                                  <SelectItem value="5-10">5-10 years</SelectItem>
                                  <SelectItem value="10+">10+ years</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* AU Utilization Bucket */}
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-400">AU Utilization</Label>
                              <Select
                                value={au.au_utilization_bucket || "<10"}
                                onValueChange={(v) => {
                                  updateAU(idx, "au_utilization_bucket", v);
                                  // Also update legacy util field
                                  const utilMap = { "<10": 5, "10-30": 20, "30-70": 50, "70+": 80 };
                                  updateAU(idx, "au_utilization", utilMap[v] || 5);
                                }}
                              >
                                <SelectTrigger className="h-9 bg-zinc-700 border-zinc-600" data-testid={`au-util-bucket-${idx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="<10">&lt;10% (Excellent)</SelectItem>
                                  <SelectItem value="10-30">10-30% (Good)</SelectItem>
                                  <SelectItem value="30-70">30-70% (Fair)</SelectItem>
                                  <SelectItem value="70+">70%+ (Poor)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Credit Limit */}
                            <div className="space-y-1">
                              <Label className="text-xs text-zinc-400">Credit Limit ($)</Label>
                              <Input
                                type="number"
                                min={0}
                                value={au.au_credit_limit}
                                onChange={(e) => updateAU(idx, "au_credit_limit", parseInt(e.target.value) || 0)}
                                className="h-9 bg-zinc-700 border-zinc-600"
                                data-testid={`au-limit-${idx}`}
                              />
                            </div>
                            
                            {/* Has Lates Toggle */}
                            <div className="flex items-center gap-2 pt-5">
                              <Switch
                                checked={au.au_has_lates || false}
                                onCheckedChange={(v) => {
                                  updateAU(idx, "au_has_lates", v);
                                  updateAU(idx, "au_is_clean", !v);
                                }}
                                id={`au-lates-${idx}`}
                                data-testid={`au-lates-${idx}`}
                              />
                              <Label htmlFor={`au-lates-${idx}`} className="text-xs">
                                Has Late Payments
                              </Label>
                            </div>
                          </div>
                          
                          {/* AU Quality Indicator */}
                          <div className={`text-xs p-2 rounded flex items-center gap-2 ${
                            !au.au_has_lates && (au.au_age_bucket === "10+" || au.au_age_bucket === "5-10") && (au.au_utilization_bucket === "<10")
                              ? "bg-emerald-500/10 text-emerald-400"
                              : au.au_has_lates
                              ? "bg-red-500/10 text-red-400"
                              : "bg-amber-500/10 text-amber-400"
                          }`}>
                            {!au.au_has_lates && (au.au_age_bucket === "10+" || au.au_age_bucket === "5-10") && au.au_utilization_bucket === "<10" ? (
                              <>
                                <CheckCircle className="w-3 h-3" />
                                High-quality AU tradeline - maximum benefit
                              </>
                            ) : au.au_has_lates ? (
                              <>
                                <AlertTriangle className="w-3 h-3" />
                                Warning: AU with late payments may hurt score or have no effect
                              </>
                            ) : (
                              <>
                                <Info className="w-3 h-3" />
                                Moderate AU quality - some benefit expected
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Add AU Button */}
                    <Button
                      variant="outline"
                      onClick={addAU}
                      className="w-full border-dashed border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      data-testid="add-au-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add AU Tradeline
                    </Button>

                    {/* AU Tips */}
                    {authorizedUsers.length > 0 && (
                      <div className="text-xs text-purple-300/70 p-3 bg-purple-500/10 rounded">
                        <Info className="w-3 h-3 inline mr-1" />
                        Best AU tradelines: 5+ years history, $10K+ limit, &lt;10% utilization, no lates.
                        Diminishing returns apply to additional AUs.
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={runSimulation}
                disabled={simulating}
                className="flex-1 h-14 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="calculate-projection-btn"
              >
                {simulating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculate Projection
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={resetForm}
                className="h-14 px-4"
                data-testid="reset-form-btn"
              >
                <RefreshCw className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-4">
            {results ? (
              <>
                {/* Per-Bureau Results */}
                <Card className="border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-green-400">
                      <Target className="w-5 h-5" />
                      Score Projections
                      <Badge variant="outline" className="ml-2 text-xs">
                        {results.enabledBureaus?.length || 0} Bureau{(results.enabledBureaus?.length || 0) !== 1 ? 's' : ''}
                      </Badge>
                    </CardTitle>
                    
                    {/* 10% Utilization Toggle */}
                    {results.has10PercentProjection && (
                      <div className="flex items-center gap-3 mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <Switch
                          checked={show10PercentProjection}
                          onCheckedChange={setShow10PercentProjection}
                          id="show-10-percent"
                          data-testid="show-10-percent-toggle"
                        />
                        <Label htmlFor="show-10-percent" className="text-sm text-blue-400 cursor-pointer">
                          Compare with 10% Utilization Score
                        </Label>
                        {show10PercentProjection && (
                          <Badge className="bg-blue-500/30 text-blue-300 text-xs">
                            Comparison ON
                          </Badge>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {/* Horizontal grid for bureau results - responsive */}
                    <div className={`grid gap-4 ${
                      (results.enabledBureaus || BUREAUS).filter(b => results.bureaus[b]).length === 1 
                        ? 'grid-cols-1' 
                        : (results.enabledBureaus || BUREAUS).filter(b => results.bureaus[b]).length === 2 
                          ? 'grid-cols-1 md:grid-cols-2' 
                          : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                    }`}>
                    {/* Show only enabled bureaus */}
                    {(results.enabledBureaus || BUREAUS).map((bureau) => {
                      const data = results.bureaus[bureau];
                      const data10 = results.bureaus10Percent?.[bureau];
                      if (!data) return null;
                      
                      const bureauColors = {
                        EQUIFAX: 'border-l-red-500',
                        EXPERIAN: 'border-l-blue-500',
                        TRANSUNION: 'border-l-green-500',
                      };
                      
                      return (
                        <div key={bureau} className={`p-4 bg-zinc-800/50 rounded-lg border-l-4 ${bureauColors[bureau]}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-medium text-zinc-300">{bureau}</span>
                            <div className="flex items-center gap-2">
                              <Badge className={`${getScoreBgColor(data.projected_expected)} px-2 py-0.5`}>
                                {data.projected_tier}
                              </Badge>
                              {show10PercentProjection && data10 && (
                                <Badge className="bg-blue-500/30 text-blue-300 px-2 py-0.5">
                                  @ 10%: {data10.projected_tier}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Regular Projection */}
                          <div className="flex items-center justify-around">
                            <div className="text-center">
                              <p className="text-xs text-zinc-500">Current</p>
                              <p className={`text-2xl font-bold ${getScoreColor(data.current_score)}`}>
                                {data.current_score}
                              </p>
                            </div>
                            <div className="flex flex-col items-center">
                              <TrendingUp className="w-6 h-6 text-green-400" />
                              <span className="text-green-400 font-bold text-sm">
                                +{Math.max(0, data.delta_expected - 15)} to +{data.delta_expected + 15}
                              </span>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-zinc-500">Projected Range</p>
                              <p className={`text-xl font-bold ${getScoreColor(data.projected_expected)}`}>
                                {Math.max(300, data.projected_expected - 15)} - {Math.min(850, data.projected_expected + 15)}
                              </p>
                            </div>
                          </div>
                          
                          {/* 10% Utilization Projection (when toggle is ON) */}
                          {show10PercentProjection && data10 && (
                            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Target className="w-4 h-4 text-blue-400" />
                                <span className="text-sm font-semibold text-blue-400">
                                  With 10% Utilization
                                </span>
                              </div>
                              <div className="flex items-center justify-around">
                                <div className="text-center">
                                  <p className="text-xs text-zinc-500">Current</p>
                                  <p className={`text-lg font-bold ${getScoreColor(data.current_score)}`}>
                                    {data.current_score}
                                  </p>
                                </div>
                                <div className="flex flex-col items-center">
                                  <TrendingUp className="w-5 h-5 text-blue-400" />
                                  <span className="text-blue-400 font-bold text-sm">
                                    +{Math.max(0, data10.delta_expected - 15)} to +{data10.delta_expected + 15}
                                  </span>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-blue-400">@ 10% Util</p>
                                  <p className={`text-lg font-bold text-blue-400`}>
                                    {Math.max(300, data10.projected_expected - 15)} - {Math.min(850, data10.projected_expected + 15)}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Difference highlight */}
                              {data10.projected_expected > data.projected_expected && (
                                <div className="mt-2 text-center text-xs text-green-400 bg-green-500/10 p-2 rounded">
                                  💡 Paying to 10% could add <strong>+{data10.projected_expected - data.projected_expected} more points</strong>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </div>
                  </CardContent>
                </Card>

                {/* NEW: Detailed Impact Breakdown per Bureau */}
                {results.impactBreakdowns && Object.keys(results.impactBreakdowns).length > 0 && (
                  <Card className="border-purple-500/20 bg-purple-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-purple-400">
                        <Scale className="w-5 h-5" />
                        Detailed Impact Breakdown
                      </CardTitle>
                      <p className="text-xs text-zinc-500">How each factor affects your projected score</p>
                    </CardHeader>
                    <CardContent>
                      {/* Horizontal grid for impact breakdown - responsive */}
                      <div className={`grid gap-4 ${
                        (results.enabledBureaus || BUREAUS).filter(b => results.impactBreakdowns[b]).length === 1 
                          ? 'grid-cols-1' 
                          : (results.enabledBureaus || BUREAUS).filter(b => results.impactBreakdowns[b]).length === 2 
                            ? 'grid-cols-1 md:grid-cols-2' 
                            : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                      }`}>
                      {(results.enabledBureaus || BUREAUS).map((bureau) => {
                        const breakdown = results.impactBreakdowns[bureau];
                        if (!breakdown) return null;
                        
                        const bureauColors = {
                          EQUIFAX: { text: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/5' },
                          EXPERIAN: { text: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/5' },
                          TRANSUNION: { text: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/5' },
                        };
                        const colors = bureauColors[bureau];
                        
                        const factors = [
                          { label: 'Starting Score', value: breakdown.starting_score, isBase: true },
                          { label: 'Derogatory Removal', value: breakdown.derogatory_removal_impact, icon: AlertTriangle },
                          { label: 'Remaining Derogs Penalty', value: breakdown.remaining_derog_penalty, icon: AlertCircle, isNegative: true },
                          { label: 'Utilization Improvement', value: breakdown.utilization_impact, icon: Percent },
                          { label: 'Authorized User Tradelines', value: breakdown.authorized_user_impact, icon: Users },
                          { label: 'Inquiry Impact', value: breakdown.inquiry_impact, icon: Clock },
                        ].filter(f => f.isBase || f.value !== 0);
                        
                        return (
                          <div key={bureau} className={`p-4 rounded-lg border ${colors.border} ${colors.bg}`}>
                            <div className="flex items-center justify-between mb-3">
                              <span className={`font-semibold ${colors.text}`}>{bureau}</span>
                              <Badge className={`${getScoreBgColor(breakdown.projected_score)} text-xs`}>
                                {breakdown.starting_score} → {Math.max(300, breakdown.projected_score - 15)}-{Math.min(850, breakdown.projected_score + 15)}
                              </Badge>
                            </div>
                            
                            <div className="space-y-2">
                              {factors.map((factor, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    {factor.icon && <factor.icon className="w-4 h-4 text-zinc-500" />}
                                    <span className="text-zinc-400">{factor.label}</span>
                                  </div>
                                  <span className={`font-mono font-medium ${
                                    factor.isBase ? 'text-zinc-300' :
                                    factor.value > 0 ? 'text-green-400' : 
                                    factor.value < 0 ? 'text-red-400' : 'text-zinc-500'
                                  }`}>
                                    {factor.isBase ? factor.value : (factor.value > 0 ? '+' : '') + factor.value}
                                  </span>
                                </div>
                              ))}
                              
                              {/* Total Line - Show Range */}
                              <div className="flex items-center justify-between pt-2 mt-2 border-t border-zinc-700">
                                <span className="font-semibold text-zinc-300">Estimated Change</span>
                                <span className={`font-bold ${breakdown.total_change > 0 ? 'text-green-400' : breakdown.total_change < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                                  {breakdown.total_change > 0 ? '+' : ''}{Math.max(0, breakdown.total_change - 15)} to {breakdown.total_change > 0 ? '+' : ''}{breakdown.total_change + 15} pts
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Credit Card Impact */}
                {results.cards && results.cards.length > 0 && (
                  <Card className="border-green-500/20 bg-green-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-green-400">
                        <Wallet className="w-5 h-5" />
                        Credit Utilization Impact
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {results.cards.map((card, idx) => {
                        const hasPaydown = card.paydown_amount > 0;
                        const hasImpact = card.estimated_impact !== 0;
                        const isHighUtil = card.current_utilization >= 30;
                        
                        return (
                          <div key={idx} className="p-3 bg-zinc-800/30 rounded-lg space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-zinc-300 text-sm font-medium">{card.name || `Card ${idx + 1}`}</span>
                              {hasImpact ? (
                                <Badge className={`${card.estimated_impact > 0 ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                                  {card.estimated_impact > 0 ? '+' : ''}{card.estimated_impact} pts
                                </Badge>
                              ) : (
                                <Badge className="bg-zinc-500/20 text-zinc-400">No change</Badge>
                              )}
                            </div>
                            
                            {/* Balance and Utilization Info */}
                            <div className="flex items-center justify-between text-xs">
                              <div className="text-zinc-500">
                                ${card.current_balance.toLocaleString()} / ${card.credit_limit.toLocaleString()} limit
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`font-bold ${card.current_utilization >= 75 ? 'text-red-400' : card.current_utilization >= 50 ? 'text-orange-400' : card.current_utilization >= 30 ? 'text-amber-400' : 'text-green-400'}`}>
                                  {card.current_utilization}%
                                </span>
                                {hasPaydown && (
                                  <>
                                    <ArrowRight className="w-3 h-3 text-zinc-500" />
                                    <span className={`font-bold ${card.target_utilization >= 75 ? 'text-red-400' : card.target_utilization >= 50 ? 'text-orange-400' : card.target_utilization >= 30 ? 'text-amber-400' : 'text-green-400'}`}>
                                      {card.target_utilization}%
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {/* Current Utilization Penalty Warning */}
                            {!hasPaydown && isHighUtil && (
                              <div className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                High utilization ({card.current_utilization}%) is hurting your score by ~{Math.abs(card.current_penalty)} pts
                              </div>
                            )}
                            
                            {/* Paydown Suggestion */}
                            {hasPaydown && (
                              <div className="text-xs text-green-400">
                                Pay down ${card.paydown_amount.toLocaleString()} to improve score
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Total Utilization Summary */}
                      {(() => {
                        const totalLimit = results.cards.reduce((sum, c) => sum + c.credit_limit, 0);
                        const totalCurrentBalance = results.cards.reduce((sum, c) => sum + c.current_balance, 0);
                        const totalTargetBalance = results.cards.reduce((sum, c) => sum + c.target_balance, 0);
                        const totalCurrentUtil = totalLimit > 0 ? Math.round((totalCurrentBalance / totalLimit) * 100) : 0;
                        const totalTargetUtil = totalLimit > 0 ? Math.round((totalTargetBalance / totalLimit) * 100) : 0;
                        const totalImpact = results.cards.reduce((sum, c) => sum + c.estimated_impact, 0);
                        
                        return (
                          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mt-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-green-400 font-medium">Total Utilization</span>
                              <div className="flex items-center gap-2">
                                <span className={`font-bold ${totalCurrentUtil >= 30 ? 'text-amber-400' : 'text-green-400'}`}>
                                  {totalCurrentUtil}%
                                </span>
                                {totalCurrentBalance !== totalTargetBalance && (
                                  <>
                                    <ArrowRight className="w-4 h-4 text-zinc-500" />
                                    <span className={`font-bold ${totalTargetUtil >= 30 ? 'text-amber-400' : 'text-green-400'}`}>
                                      {totalTargetUtil}%
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            {totalImpact !== 0 && (
                              <div className="text-xs text-green-300 mt-1">
                                Total estimated impact: {totalImpact > 0 ? '+' : ''}{totalImpact} points
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}

                {/* Credit Optimization Advisor */}
                {results.cards && results.cards.length > 0 && (
                  <Card className="border-amber-500/20 bg-amber-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-amber-400">
                        <Sparkles className="w-5 h-5" />
                        Credit Optimization Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CreditOptimizationAdvisor
                        creditCards={results.cards.map(c => ({
                          name: c.name || 'Credit Card',
                          balance: c.current_balance || 0,
                          limit: c.credit_limit || 0
                        }))}
                        currentScore={Object.values(results.bureaus || {})[0]?.current || null}
                        showTitle={false}
                        compact={true}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Derogatory Removal Impact - NEW SECTION */}
                {results.derogBreakdowns && Object.values(results.derogBreakdowns).some(b => b.length > 0) && (
                  <Card className="border-red-500/20 bg-red-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-red-400">
                        <AlertTriangle className="w-5 h-5" />
                        Derogatory Removal Impact
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {BUREAUS.map((bureau) => {
                        const breakdown = results.derogBreakdowns[bureau];
                        if (!breakdown || breakdown.length === 0) return null;
                        
                        const totalImpact = breakdown.reduce((sum, item) => sum + item.total_impact, 0);
                        
                        return (
                          <div key={bureau} className="p-3 bg-zinc-800/30 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-zinc-300 text-sm">{bureau}</span>
                              <Badge className="bg-red-500/20 text-red-400">
                                +{totalImpact} pts total
                              </Badge>
                            </div>
                            <div className="space-y-1">
                              {breakdown.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs">
                                  <div className="text-zinc-400">
                                    <span className="text-zinc-300 font-medium">{item.count}x</span> {item.label}
                                  </div>
                                  <div className="text-green-400 font-medium">
                                    +{item.total_impact} pts
                                    <span className="text-zinc-500 ml-1">
                                      ({item.count} × {item.impact_per_item})
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-700">
                        Impact shown is estimated points recovered when items are removed/deleted
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AU Tradeline Impact */}
                {results.authorizedUsers && results.authorizedUsers.length > 0 && (
                  <Card className="border-purple-500/20 bg-purple-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-purple-400">
                        <Users className="w-5 h-5" />
                        AU Tradeline Impact
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {results.authorizedUsers.map((au, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
                          <div>
                            <span className="text-zinc-300 text-sm">{au.card_name}</span>
                            <div className="text-xs text-zinc-500">
                              {au.history_years} yrs • ${au.credit_limit.toLocaleString()} limit • {au.utilization}% util
                              {au.is_clean && " • Clean"}
                            </div>
                          </div>
                          <Badge className="bg-purple-500/20 text-purple-400">
                            +{au.estimated_impact} pts
                          </Badge>
                        </div>
                      ))}
                      <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-700">
                        Note: Additional AUs have diminishing returns
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={sendEmail}
                    disabled={sendingEmail || !clientEmail}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="email-results-btn"
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Email Results to Client
                      </>
                    )}
                  </Button>
                </div>

                {/* Disclaimer */}
                <p className="text-xs text-zinc-500 text-center">
                  <Info className="w-3 h-3 inline mr-1" />
                  Score projections are estimates based on {results.model} model. Actual results may vary.
                </p>
              </>
            ) : (
              /* Empty state */
              <Card className="border-zinc-700 bg-zinc-900/50 h-full min-h-[400px]">
                <CardContent className="h-full flex flex-col items-center justify-center py-16">
                  <Sparkles className="w-16 h-16 text-zinc-600 mb-6" />
                  <h3 className="text-xl font-medium text-zinc-400 mb-2">
                    Ready to Calculate
                  </h3>
                  <p className="text-zinc-500 text-center max-w-sm text-sm">
                    Enter current scores, derogatory items, credit cards to pay down, 
                    and AU tradelines to see projected improvements across all bureaus.
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-zinc-500">
                    <div className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Per-bureau projections
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Card paydown impact
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      AU tradeline calculator
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Email to client
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
